import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Grid,
  Paper as MuiPaper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  IconButton,
  Chip,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  Skeleton,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  CircularProgress,
  FormHelperText,
  InputAdornment,
  OutlinedInput,
  Alert,
  Zoom,
  Fade,
  Slide,
  alpha,
  Avatar,
  Divider,
  FormControlLabel,
  Autocomplete,
  LinearProgress,
  ListItemButton,
  ListItemSecondaryAction,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon,
  Description as DescriptionIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Close as CloseIcon,
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon,
  FilterList as FilterListIcon,
  Search as SearchIcon,
  Category as CategoryIcon,
  SaveAlt as SaveIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckIcon,
  Info as InfoIcon,
  RemoveRedEye as PreviewIcon,
  Image as ImageIcon,
  Quiz as QuizIcon,
  KeyboardArrowUp as ArrowUpIcon,
  KeyboardArrowDown as ArrowDownIcon,
  Subject as SectionIcon,
  DragIndicator as DragIndicatorIcon,
  Clear as ClearIcon,
  Link as LinkIcon,
  TravelExplore as TravelExploreIcon,
  StarBorder as StarBorderIcon,
  HealthAndSafety as HealthCheckIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import ReactQuill from 'react-quill-new';
import { QUILL_MODULES, QUILL_FORMATS, plainTextLength } from '../../utils/quillQuestionEditorConfig';
import api from '../../api';
import { adminHasPermission } from '../../utils/adminPermissions';
import { useLocation, useNavigate } from 'react-router-dom';
import HtmlWithLatex from '../QuestionBank/components/HtmlWithLatex';
import QuestionBankQuestionEditDialog from '../QuestionBank/components/QuestionBankQuestionEditDialog';
import ConvertToMcqDialog from '../QuestionBank/components/ConvertToMcqDialog';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Validation schema
const paperSchema = yup.object().shape({
  name: yup.string()
    .required('Paper name is required')
    .max(255, 'Name must not exceed 255 characters'),
  description: yup.string()
    .nullable()
    .transform((value, originalValue) => {
      return originalValue === '' ? null : value;
    }),
  original_paper_link: yup.string()
    .nullable()
    .max(2048, 'Original paper link must not exceed 2048 characters')
    .transform((value, originalValue) => {
      return originalValue === '' ? null : value;
    }),
  category_id: yup.number()
    .required('Category is required')
    .integer('Category must be an integer')
    .transform((value, originalValue) => {
      return originalValue === '' ? undefined : value;
    })
    .typeError('Category must be selected'),
  format: yup.number()
    .required('Format is required')
    .integer('Format must be an integer')
    .transform((value, originalValue) => {
      return originalValue === '' ? undefined : value;
    })
    .typeError('Format must be selected'),
  school_year_id: yup.number()
    .required('School year is required')
    .integer('School year must be an integer')
    .transform((value, originalValue) => (originalValue === '' ? undefined : value))
    .typeError('School year must be selected'),
  subject_ids: yup
    .array()
    .of(yup.number().integer())
    .min(1, 'Select at least one subject')
    .required('Select at least one subject'),
  price: yup.number()
    .required('Price is required')
    .min(0, 'Price must be at least 0')
    .typeError('Price must be a number'),
  currency: yup.string()
    .nullable()
    .length(1, 'Currency must be exactly 1 character')
    .oneOf(['€', '$', '£'], 'Currency must be one of: €, $, £')
    .transform((value, originalValue) => {
      return originalValue === '' ? null : value;
    }),
  duration_minutes: yup.number()
    .nullable()
    .min(1, 'Duration must be at least 1 minute')
    .integer('Duration must be a whole number')
    .transform((value, originalValue) => (originalValue === '' || originalValue == null ? null : value))
    .typeError('Duration must be a number'),
  paper_image: yup.mixed()
    .nullable()
    .test('fileSize', 'Paper image size must not exceed 10MB', (value) => {
      if (!value) return true;
      return value.size <= 10 * 1024 * 1024;
    })
    .test('fileType', 'The uploaded file must be an image', (value) => {
      if (!value) return true;
      return value.type.startsWith('image/');
    })
});

const emptyPaperListFilters = {
  category_id: '',
  format: '',
  search: '',
  school_year_id: '',
  subject_id: '',
  school_id: '',
  status: '',
  has_source_link: '',
};

const SortablePaperQuestionRow = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        position: 'relative',
      }}
    >
      {children({ dragHandleProps: { ...attributes, ...listeners } })}
    </div>
  );
};

const SortableSubQuestionRow = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
      }}
    >
      {children({ dragHandleProps: { ...attributes, ...listeners } })}
    </div>
  );
};

// ─── CreateQuestionDialog ───────────────────────────────────────────────────────
const CreateQuestionDialog = ({ open, onClose, mode, targetQ, onSave, saving }) => {
  if (!open) return null;
  const label =
    mode === 'sub-question'
      ? `New sub-question for Q${targetQ?.paper_number ?? ''}`
      : mode === 'before'
      ? `New question before Q${targetQ?.paper_number ?? '?'}`
      : `New question after Q${targetQ?.paper_number ?? '?'}`;
  const blankQuestion = {
    id: null,
    type: 'multiple-choice',
    question_text: '',
    difficulty: 'Medium',
    default_marks: 1,
    options: [],
    answer: null,
    sub_questions: [],
    image_url: null,
    image_urls: [],
    image_description: '',
    explanation: '',
    section_title: '',
    section_instructions: '',
    topic: '',
    topic_ids: [],
    subject_id: null,
    import_id: null,
    is_sample_question: false,
    sub_label_type: 'letter',
  };
  return (
    <QuestionBankQuestionEditDialog
      question={blankQuestion}
      onClose={onClose}
      onSave={onSave}
      saving={saving}
      createMode
      createModeLabel={label}
    />
  );
};

const isFillQuestionType = (type) => {
  const t = String(type || '').toLowerCase().replace(/_/g, '-');
  return t === 'multi-fill' || t === 'fill-in-the-blank' || t === 'table';
};

const getFixedFillTotal = (q) => {
  const ad = q?.answer_data ?? {};
  const ans = q?.answer ?? {};
  if ((ad.marks_mode ?? ans.marks_mode) !== 'fixed') return null;
  const total = ad.marks_total ?? ans.marks_total ?? q?.default_marks;
  const t = parseInt(total, 10) || 0;
  return t > 0 ? t : null;
};

const getQuestionEffectiveMarks = (q) => {
  if (isFillQuestionType(q?.type)) {
    const fixed = getFixedFillTotal(q);
    if (fixed != null) return fixed;
    const blanks = q?.answer_data?.blank_marks ?? q?.answer?.blank_marks;
    if (Array.isArray(blanks) && blanks.length > 0) {
      return blanks.reduce((s, m) => s + (parseInt(m, 10) || 0), 0);
    }
  }
  return parseInt(q?.default_marks, 10) || 0;
};

const getQuestionMarksChipLabel = (q) => {
  if (isFillQuestionType(q?.type)) {
    const fixed = getFixedFillTotal(q);
    if (fixed != null) return `${fixed}m`;
    const blanks = q?.answer_data?.blank_marks ?? q?.answer?.blank_marks;
    if (Array.isArray(blanks) && blanks.length > 0) {
      const total = blanks.reduce((s, m) => s + (parseInt(m, 10) || 0), 0);
      const first = parseInt(blanks[0], 10) || 0;
      const allEqual = blanks.every((m) => (parseInt(m, 10) || 0) === first);
      return allEqual && first > 0 ? `${first}m × ${blanks.length}` : `${total}m`;
    }
  }
  const m = parseInt(q?.default_marks, 10) || 1;
  return `${m}m`;
};

const Paper = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const canViewPapers = adminHasPermission('view-papers');
  const canCreatePapers = adminHasPermission('create-papers');
  const canUpdatePapers = adminHasPermission('update-papers');
  const canDeletePapers = adminHasPermission('delete-papers');
  const canDeleteQuestionBank = adminHasPermission('delete-question-bank');
  const hasCreatePaperTab = canCreatePapers;
  const showPaperFormTab = canCreatePapers || canUpdatePapers;

  const [papers, setPapers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [formats, setFormats] = useState([]);
  const [subjectsList, setSubjectsList] = useState([]);
  const [schoolYears, setSchoolYears] = useState([]);
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingPaper, setEditingPaper] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [filters, setFilters] = useState(() => ({ ...emptyPaperListFilters }));
  const [appliedFilters, setAppliedFilters] = useState(() => ({ ...emptyPaperListFilters }));
  const [viewDialog, setViewDialog] = useState(false);
  const [mcqConvertPaper, setMcqConvertPaper] = useState(null);
  const [viewingPaper, setViewingPaper] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    paper: null,
    deleteQuestionBankToo: false,
    forceDelete: false,
  });
  const [deletingPaperId, setDeletingPaperId] = useState(null);
  const [removeQDialog, setRemoveQDialog] = useState({ open: false, question: null });
  const [togglingPaperId, setTogglingPaperId] = useState(null);
  const [viewingPdfPaperId, setViewingPdfPaperId] = useState(null);
  const [downloadingAnswerKeyPaperId, setDownloadingAnswerKeyPaperId] = useState(null);
  const [existingImageUrl, setExistingImageUrl] = useState(null);
  const [formSourceLinks, setFormSourceLinks] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalPapers, setTotalPapers] = useState(0);

  // Assign question bank → paper extract (Mock Exam–style picker)
  const [assignQbOpen, setAssignQbOpen] = useState(false);
  const [assignQbPaper, setAssignQbPaper] = useState(null);
  const [assignQbSelected, setAssignQbSelected] = useState([]);
  const [assignQbLoading, setAssignQbLoading] = useState(false);
  const [assignQbSaving, setAssignQbSaving] = useState(false);
  const [bankPickerOpen, setBankPickerOpen] = useState(false);
  const [bankPickerQuestions, setBankPickerQuestions] = useState([]);
  const [bankPickerLoading, setBankPickerLoading] = useState(false);
  const [bankPickerPage, setBankPickerPage] = useState(0);
  const [bankPickerTotal, setBankPickerTotal] = useState(0);
  const [bankPickerSearch, setBankPickerSearch] = useState('');
  const [bankPickerType, setBankPickerType] = useState('');
  const [bankPickerTopicIds, setBankPickerTopicIds] = useState([]);
  const [bankPickerSelected, setBankPickerSelected] = useState([]);
  
  const [inlineSectionEdit, setInlineSectionEdit] = useState(null);
  const [inlineSectionSaving, setInlineSectionSaving] = useState(false);
  const [qSectionEdit, setQSectionEdit] = useState(null); // { qId, title, instructions }
  const [qSectionSaving, setQSectionSaving] = useState(false);
  const [removeImage, setRemoveImage] = useState(false);
  // Manual questions (Create Paper - add questions one by one)
  const [questions, setQuestions] = useState([
    { question_text: '', options: ['', ''], correct_answer: '', marks: 1, duration_in_sec: 60, is_free: false, topic_ids: [] }
  ]);
  /** Question bank rows synced to PaperExtract (shown on edit when legacy paper_questions are empty) */
  const [questionBankExtractPreview, setQuestionBankExtractPreview] = useState([]);
  const [editingBankQuestion, setEditingBankQuestion] = useState(null);
  const [bankQuestionSaving, setBankQuestionSaving] = useState(false);
  const [paperExtractRemovingId, setPaperExtractRemovingId] = useState(null);
  const [scalingMarks, setScalingMarks] = useState(false);
  const [removingSubQId, setRemovingSubQId] = useState(null);
  const [removeSubQDialog, setRemoveSubQDialog] = useState({ open: false, parentQ: null, subIdx: null, label: '' });
  const [editingPaperNumbers, setEditingPaperNumbers] = useState({});
  const [missingSlotNumber, setMissingSlotNumber] = useState(null);
  const [addSubQParent, setAddSubQParent] = useState(null);
  const [addQDialog, setAddQDialog] = useState({ open: false, mode: null, targetIdx: null, parentQ: null });
  const [addQSaving, setAddQSaving] = useState(false);

  // Preview states
  const [imagePreview, setImagePreview] = useState({ open: false, url: null });

  // Source-link deep research
  const [findLinkPaper, setFindLinkPaper]                   = useState(null);
  const [findLinkCandidates, setFindLinkCandidates]         = useState([]);
  const [findLinkLoading, setFindLinkLoading]               = useState(false);
  const [findLinkDialogOpen, setFindLinkDialogOpen]         = useState(false);
  const [findLinkSaving, setFindLinkSaving]                 = useState(false);
  const [findLinkError, setFindLinkError]                   = useState(false);
  const [linkCheckRunning, setLinkCheckRunning]             = useState(false);
  const [reSearchRunning, setReSearchRunning]               = useState(false);
  const [batchLinkStatus, setBatchLinkStatus]               = useState(null);
  const [batchLinkRunning, setBatchLinkRunning]             = useState(false);
  const batchPollRef                                        = useRef(null);
  const [viewLinkEdit, setViewLinkEdit]                     = useState(null);
  const [viewLinkSaving, setViewLinkSaving]                 = useState(false);

  // Form handling
  const { control, handleSubmit, reset, watch, setValue, formState: { errors } } = useForm({
    resolver: yupResolver(paperSchema),
    defaultValues: {
      name: '',
      description: null,
      original_paper_link: '',
      category_id: '',
      format: '',
      school_year_id: '',
      subject_ids: [],
      target_school_ids: [],
      price: '',
      currency: '£',
      paper_image: null,
      is_free_paper: false,
      due_at: '',
      duration_minutes: '',
    }
  });

  const subjectIdsWatch = watch('subject_ids');
  const schoolYearWatch = watch('school_year_id');
  const watchedPrice = watch('price');
  const watchedCurrency = watch('currency');
  const [topicOptions, setTopicOptions] = useState([]);

  const gapsByIndex = useMemo(() => {
    const result = {};
    for (let idx = 0; idx < questionBankExtractPreview.length - 1; idx++) {
      const currNum = parseInt(questionBankExtractPreview[idx]?.paper_number, 10);
      const nextNum = parseInt(questionBankExtractPreview[idx + 1]?.paper_number, 10);
      if (!Number.isNaN(currNum) && !Number.isNaN(nextNum) && nextNum - currNum > 1) {
        result[idx] = Array.from({ length: nextNum - currNum - 1 }, (_, i) => currNum + 1 + i);
      }
    }
    return result;
  }, [questionBankExtractPreview]);

  // Align with public catalogue: price 0 ⇒ free past paper (same as FrontendController).
  useEffect(() => {
    const n = parseFloat(String(watchedPrice ?? "").replace(",", "."));
    if (Number.isNaN(n)) return;
    if (n <= 0) {
      setValue("is_free_paper", true, { shouldDirty: true, shouldValidate: true });
    } else {
      setValue("is_free_paper", false, { shouldDirty: true, shouldValidate: true });
    }
  }, [watchedPrice, setValue]);

  useEffect(() => {
    let cancelled = false;
    const loadTopics = async () => {
      const subj = subjectIdsWatch;
      const sy = schoolYearWatch;
      if (!subj || !Array.isArray(subj) || subj.length === 0 || sy === '' || sy === undefined || sy === null) {
        setTopicOptions([]);
        return;
      }
      try {
        const params = new URLSearchParams();
        subj.forEach((id) => {
          if (id !== '' && id != null && !Number.isNaN(Number(id))) {
            params.append('subject_ids[]', Number(id));
          }
        });
        params.append('school_year_id', Number(sy));
        const res = await api.get(`admin/paper/available-topics?${params.toString()}`);
        const data = res.data?.data;
        if (!cancelled) {
          setTopicOptions(Array.isArray(data) ? data : []);
        }
      } catch (e) {
        console.error('Failed to load topics', e);
        if (!cancelled) setTopicOptions([]);
      }
    };
    loadTopics();
    return () => {
      cancelled = true;
    };
  }, [subjectIdsWatch, schoolYearWatch]);

  const addQuestion = () => {
    setQuestions(prev => [...prev, { question_text: '', options: ['', ''], correct_answer: '', marks: 1, duration_in_sec: 60, is_free: false, topic_ids: [] }]);
  };
  const removeQuestion = (qIndex) => {
    if (questions.length <= 1) {
      toast.error('At least one question is required');
      return;
    }
    setQuestions(prev => prev.filter((_, i) => i !== qIndex));
  };
  const updateQuestion = (qIndex, field, value) => {
    setQuestions(prev => prev.map((q, i) => i === qIndex ? { ...q, [field]: value } : q));
  };
  const addOption = (qIndex) => {
    setQuestions(prev => prev.map((q, i) => i === qIndex ? { ...q, options: [...q.options, ''] } : q));
  };
  const removeOption = (qIndex, optIndex) => {
    setQuestions(prev => prev.map((q, i) => {
      if (i !== qIndex) return q;
      if (q.options.length <= 2) return q;
      const newOpts = q.options.filter((_, j) => j !== optIndex);
      return { ...q, options: newOpts, correct_answer: q.correct_answer === q.options[optIndex] ? '' : q.correct_answer };
    }));
  };
  const updateOption = (qIndex, optIndex, value) => {
    setQuestions(prev => prev.map((q, i) => {
      if (i !== qIndex) return q;
      const newOpts = [...q.options];
      newOpts[optIndex] = value;
      return { ...q, options: newOpts };
    }));
  };

  // Preview image
  const handleImagePreview = (file) => {
    if (file instanceof File) {
      const url = URL.createObjectURL(file);
      setImagePreview({ open: true, url });
    } else if (typeof file === 'string') {
      setImagePreview({ open: true, url: file });
    }
  };

  const handleCloseImagePreview = () => {
    if (imagePreview.url && imagePreview.url.startsWith('blob:')) {
      URL.revokeObjectURL(imagePreview.url);
    }
    setImagePreview({ open: false, url: null });
  };

  const loadPapers = useCallback(async (applied, pageIdx, perPage) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (applied.category_id) params.append('category_id', applied.category_id);
      if (applied.format) params.append('format', applied.format);
      if (applied.school_id) params.append('school_id', applied.school_id);
      if (applied.school_year_id) params.append('school_year_id', applied.school_year_id);
      if (applied.subject_id) params.append('subject_id', applied.subject_id);
      if (applied.status) params.append('status', applied.status);
      if (applied.has_source_link) params.append('has_source_link', applied.has_source_link);
      const q = (applied.search || '').trim();
      if (q) params.append('search', q);
      params.append('page', pageIdx + 1);
      params.append('per_page', perPage);
      const response = await api.get('admin/paper?' + params.toString());
      const paginationMeta = response.data.data;
      const papersData = paginationMeta.data || [];
      setTotalPapers(paginationMeta.total || 0);
      setPapers([...papersData].sort((a, b) => (a.name || '').localeCompare(b.name || '')));
    } catch (error) {
      console.error('Error fetching papers:', error);
      toast.error('Failed to fetch papers');
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Source-link deep research handlers ────────────────────────────────────

  const normalizeLink = (l) => (typeof l === 'string' ? { url: l, title: l } : l);

  const mergeSourceLinks = (existing, incoming) => {
    const merged = (Array.isArray(existing) ? existing : []).map(normalizeLink);
    const seen = new Set(merged.map((l) => l.url));
    let added = 0;
    (Array.isArray(incoming) ? incoming : []).map(normalizeLink).forEach((l) => {
      if (l.url && !seen.has(l.url)) {
        seen.add(l.url);
        merged.push(l);
        added++;
      }
    });
    return { merged, added };
  };

  const handleFindSourceLink = async (paper) => {
    setFindLinkPaper(paper);
    setFindLinkCandidates(Array.isArray(paper.source_links) ? paper.source_links : []);
    setFindLinkError(false);
    setFindLinkDialogOpen(true);
    setFindLinkLoading(true);
    try {
      const res = await api.post(`admin/paper/${paper.id}/find-source-links`);
      setFindLinkCandidates(res.data?.data?.candidates || []);
    } catch {
      setFindLinkError(true);
    } finally {
      setFindLinkLoading(false);
    }
  };

  const handleSelectSourceLink = async (url) => {
    if (!findLinkPaper) return;
    setFindLinkSaving(true);
    try {
      const { merged } = mergeSourceLinks(findLinkPaper.source_links, findLinkCandidates);
      await api.patch(`admin/paper/${findLinkPaper.id}/source-links`, {
        source_links: merged,
        original_paper_link: url,
      });
      setPapers((prev) =>
        prev.map((p) => p.id === findLinkPaper.id ? { ...p, original_paper_link: url, source_links: merged } : p)
      );
      setViewingPaper((prev) =>
        prev?.id === findLinkPaper.id ? { ...prev, original_paper_link: url, source_links: merged } : prev
      );
      toast.success('Source link saved');
      setFindLinkDialogOpen(false);
    } catch {
      toast.error('Failed to save source link');
    } finally {
      setFindLinkSaving(false);
    }
  };

  const handleReSearchInDetails = async () => {
    if (!viewingPaper) return;
    setReSearchRunning(true);
    try {
      const res = await api.post(`admin/paper/${viewingPaper.id}/find-source-links`);
      const candidates = res.data?.data?.candidates || [];
      const { merged, added } = mergeSourceLinks(viewingPaper.source_links, candidates);
      if (added === 0) {
        toast.info('No new links found online');
        return;
      }
      await handleViewSourceLinkSave(merged, viewingPaper.original_paper_link);
      toast.success(`Added ${added} new link${added === 1 ? '' : 's'}`);
    } catch {
      toast.error('Re-search failed — please try again');
    } finally {
      setReSearchRunning(false);
    }
  };

  const handleCheckLinks = async () => {
    if (!viewingPaper) return;
    setLinkCheckRunning(true);
    try {
      const res = await api.post(`admin/paper/${viewingPaper.id}/check-source-links`);
      const fresh = res.data?.data;
      setViewingPaper((prev) => ({ ...prev, ...fresh }));
      setPapers((prev) => prev.map((p) => p.id === viewingPaper.id ? { ...p, ...fresh } : p));
      const broken = (fresh?.source_links || []).filter((l) => l?.status === 'broken').length;
      if (broken > 0) toast.warning(`${broken} broken link${broken === 1 ? '' : 's'} found`);
      else toast.success('All links OK');
    } catch {
      toast.error('Failed to check links');
    } finally {
      setLinkCheckRunning(false);
    }
  };

  const startBatchPoll = useCallback(() => {
    clearInterval(batchPollRef.current);
    batchPollRef.current = setInterval(async () => {
      try {
        const res = await api.get('admin/paper/batch-find-source-links/status');
        const s = res.data?.data;
        setBatchLinkStatus(s);
        if (!s || s.status === 'completed' || s.status === 'failed' || s.status === 'idle') {
          clearInterval(batchPollRef.current);
          setBatchLinkRunning(false);
          if (s?.status === 'completed') {
            const notFound = s.not_found ?? (s.total - s.updated);
            toast.success(`Done: ${s.updated} updated, ${notFound} still have no link found`);
            loadPapers(appliedFilters, page, rowsPerPage);
          }
        }
      } catch { /* ignore poll errors */ }
    }, 3000);
  }, [appliedFilters, page, rowsPerPage]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBatchFindLinks = async () => {
    setBatchLinkRunning(true);
    try {
      const res = await api.post('admin/paper/batch-find-source-links');
      setBatchLinkStatus(res.data?.data);
      startBatchPoll();
    } catch (e) {
      const msg = e.response?.data?.message || '';
      if (e.response?.status === 409) {
        // Already running — just start polling
        setBatchLinkStatus(e.response.data?.data);
        startBatchPoll();
      } else {
        toast.error(msg || 'Failed to start batch job');
        setBatchLinkRunning(false);
      }
    }
  };

  const handleViewSourceLinkSave = async (updatedLinks, primaryUrl) => {
    if (!viewingPaper) return;
    setViewLinkSaving(true);
    try {
      const res = await api.patch(`admin/paper/${viewingPaper.id}/source-links`, {
        source_links: updatedLinks,
        original_paper_link: primaryUrl !== undefined ? primaryUrl : viewingPaper.original_paper_link,
      });
      const fresh = res.data?.data;
      setViewingPaper((prev) => ({ ...prev, ...fresh }));
      setPapers((prev) => prev.map((p) => p.id === viewingPaper.id ? { ...p, ...fresh } : p));
      setViewLinkEdit(null);
      toast.success('Source links updated');
    } catch {
      toast.error('Failed to update source links');
    } finally {
      setViewLinkSaving(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────

  const handleApplyListFilters = () => {
    const next = { ...filters };
    setAppliedFilters(next);
    setPage(0);
    loadPapers(next, 0, rowsPerPage);
  };

  const handleClearListFilters = () => {
    const cleared = { ...emptyPaperListFilters };
    setFilters(cleared);
    setAppliedFilters(cleared);
    setPage(0);
    loadPapers(cleared, 0, rowsPerPage);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    loadPapers(appliedFilters, newPage, rowsPerPage);
  };

  const handleRowsPerPageChange = (event) => {
    const newPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newPerPage);
    setPage(0);
    loadPapers(appliedFilters, 0, newPerPage);
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('admin/paper/category-tree');
      setCategories(response.data.data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchFormats = async () => {
    try {
      const response = await api.get('common/data?param=Formats');
      setFormats(response.data.data || []);
    } catch (error) {
      console.error('Error fetching formats:', error);
    }
  };

  const fetchSchools = async () => {
    try {
      const response = await api.get('admin/master-form/schools?per_page=1000');
      const raw = response?.data?.data;
      const list = Array.isArray(raw) ? raw : (Array.isArray(raw?.data) ? raw.data : []);
      setSchools(list);
    } catch (error) {
      console.error('Error fetching schools:', error);
      setSchools([]);
    }
  };

  const fetchSubjects = async () => {
    try {
      const response = await api.get('common/data?param=Subjects');
      setSubjectsList(response.data.data || []);
    } catch (error) {
      console.error('Error fetching subjects:', error);
      setSubjectsList([]);
    }
  };

  const fetchSchoolYears = async () => {
    try {
      const response = await api.get('common/data?param=SchoolYears');
      setSchoolYears(response.data.data || []);
    } catch (error) {
      console.error('Error fetching school years:', error);
      setSchoolYears([]);
    }
  };

  const fetchBankPickerQuestions = useCallback(async () => {
    setBankPickerLoading(true);
    try {
      const params = { page: bankPickerPage + 1, per_page: 5 };
      if (bankPickerSearch) params.search = bankPickerSearch;
      if (bankPickerType) params.type = bankPickerType;
      if (Array.isArray(bankPickerTopicIds) && bankPickerTopicIds.length > 0) {
        params.topic_ids = bankPickerTopicIds;
      }
      const res = await api.get('admin/question-bank', { params });
      const data = res.data?.data;
      setBankPickerQuestions(data?.data || []);
      setBankPickerTotal(data?.total || 0);
    } catch (err) {
      toast.error('Failed to load question bank');
    } finally {
      setBankPickerLoading(false);
    }
  }, [bankPickerPage, bankPickerSearch, bankPickerType, bankPickerTopicIds]);

  useEffect(() => {
    if (!bankPickerOpen) return undefined;
    const timer = setTimeout(() => {
      fetchBankPickerQuestions();
    }, 300);
    return () => clearTimeout(timer);
  }, [bankPickerOpen, fetchBankPickerQuestions]);

  const openAssignQuestionBank = async (paper) => {
    setAssignQbPaper(paper);
    setAssignQbOpen(true);
    setAssignQbLoading(true);
    setAssignQbSelected([]);
    try {
      const res = await api.get(`admin/paper/${paper.id}/question-bank-extract-state`);
      const data = res.data?.data;
      setAssignQbSelected(Array.isArray(data?.questions) ? data.questions : []);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load assigned questions');
    } finally {
      setAssignQbLoading(false);
    }
  };

  const closeAssignQuestionBank = () => {
    if (assignQbSaving) return;
    setAssignQbOpen(false);
    setAssignQbPaper(null);
    setAssignQbSelected([]);
  };

  const toggleBankPickerSelect = (question) => {
    setBankPickerSelected((prev) =>
      prev.some((q) => q.id === question.id)
        ? prev.filter((q) => q.id !== question.id)
        : [...prev, question]
    );
  };

  const confirmBankPickerSelection = async () => {
    if (addSubQParent && bankPickerSelected.length > 0) {
      for (const sq of bankPickerSelected) {
        try {
          await api.post(`admin/question-bank/${addSubQParent.id}/add-sub-question`, { child_id: sq.id });
        } catch (e) {
          toast.error(e.response?.data?.message || `Failed to add sub-question ${sq.id}`);
        }
      }
      try {
        const res = await api.get(`admin/question-bank/${addSubQParent.id}`);
        const updatedSubs = res.data?.data?.sub_questions || [];
        setQuestionBankExtractPreview((prev) =>
          prev.map((q) => q.id === addSubQParent.id ? { ...q, sub_questions: updatedSubs } : q)
        );
        toast.success('Sub-question(s) added');
      } catch (_) { /* ignore reload error */ }
      setAddSubQParent(null);
      setBankPickerOpen(false);
      setBankPickerSelected([]);
      setBankPickerSearch('');
      setBankPickerType('');
      setBankPickerPage(0);
      return;
    }

    setAssignQbSelected((prev) => {
      const existingIds = new Set(prev.map((q) => q.id));
      const newOnes = bankPickerSelected.filter((q) => !existingIds.has(q.id));
      return [...prev, ...newOnes];
    });

    if (missingSlotNumber !== null && bankPickerSelected.length === 1) {
      const q = bankPickerSelected[0];
      setTimeout(() => commitPaperNumber(q, String(missingSlotNumber)), 400);
    }
    setMissingSlotNumber(null);

    setBankPickerOpen(false);
    setBankPickerSelected([]);
    setBankPickerSearch('');
    setBankPickerType('');
    setBankPickerPage(0);
  };

  const removeAssignQbQuestion = (qId) => {
    setAssignQbSelected((prev) => prev.filter((q) => q.id !== qId));
  };

  const openPaperBankQuestionEdit = async (q) => {
    try {
      const res = await api.get(`admin/question-bank/${q.id}`);
      setEditingBankQuestion(res.data?.data || q);
    } catch {
      setEditingBankQuestion(q);
    }
  };

  const savePaperBankQuestionEdit = async (payload) => {
    if (!editingBankQuestion) return;
    setBankQuestionSaving(true);
    try {
      const res = await api.put(`admin/question-bank/${editingBankQuestion.id}`, payload);
      const updated = res.data?.data || { ...editingBankQuestion, ...payload };
      setQuestionBankExtractPreview((prev) =>
        prev.map((row) => (row.id === editingBankQuestion.id ? { ...row, ...updated } : row))
      );
      setEditingBankQuestion(null);
      toast.success('Question updated');
      if (canViewPapers) loadPapers(appliedFilters, page, rowsPerPage);
      if (viewDialog && viewingPaper) {
        try {
          const vRes = await api.get(`admin/paper/${viewingPaper.id}`);
          if (vRes.data?.data) setViewingPaper(vRes.data.data);
        } catch (_) {}
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update question');
    } finally {
      setBankQuestionSaving(false);
    }
  };

  const removePaperExtractQuestion = async (qId) => {
    if (!editingPaper) return;
    const next = questionBankExtractPreview.filter((q) => q.id !== qId);
    if (next.length === 0) {
      toast.error('At least one question must remain on the paper extract.');
      return;
    }
    setPaperExtractRemovingId(qId);
    try {
      await api.post(`admin/paper/${editingPaper.id}/sync-question-bank-extract`, {
        question_bank_question_ids: next.map((q) => q.id),
      });
      setQuestionBankExtractPreview(next);
      toast.success('Question removed from paper');
      if (canViewPapers) {
        loadPapers(appliedFilters, page, rowsPerPage);
      }
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to update paper');
    } finally {
      setPaperExtractRemovingId(null);
    }
  };

  const removeSubQuestion = (parentQ, subIdx) => {
    const sub = parentQ.sub_questions[subIdx];
    const subLabel = sub.paper_number != null && String(sub.paper_number).trim() !== ''
      ? String(sub.paper_number)
      : `${String.fromCharCode(97 + subIdx)})`;
    setRemoveSubQDialog({ open: true, parentQ, subIdx, label: subLabel });
  };

  const confirmRemoveSubQuestion = async () => {
    const { parentQ, subIdx } = removeSubQDialog;
    const sub = parentQ.sub_questions[subIdx];
    setRemoveSubQDialog((prev) => ({ ...prev, open: false }));
    setRemovingSubQId(sub.id);
    try {
      await api.delete(`admin/question-bank/${sub.id}`);
      setQuestionBankExtractPreview((prev) =>
        prev.map((q) =>
          q.id === parentQ.id
            ? { ...q, sub_questions: q.sub_questions.filter((_, i) => i !== subIdx) }
            : q
        )
      );
      toast.success('Sub-question removed');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to remove sub-question');
    } finally {
      setRemovingSubQId(null);
    }
  };

  const commitPaperNumber = async (q, draft) => {
    const trimmed = (draft ?? '').trim() || null;
    if (String(trimmed ?? '') === String(q.paper_number ?? '')) return;
    try {
      await api.patch(`admin/question-bank/${q.id}`, { paper_number: trimmed });
      setQuestionBankExtractPreview((prev) =>
        prev.map((row) => {
          if (row.id === q.id) return { ...row, paper_number: trimmed };
          if (Array.isArray(row.sub_questions)) {
            const sub = row.sub_questions.find((s) => s.id === q.id);
            if (sub) return { ...row, sub_questions: row.sub_questions.map((s) => s.id === q.id ? { ...s, paper_number: trimmed } : s) };
          }
          return row;
        })
      );
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to update paper number');
      setEditingPaperNumbers((prev) => ({ ...prev, [q.id]: String(q.paper_number ?? '') }));
    }
  };

  const commitInlineSectionEdit = async () => {
    if (!inlineSectionEdit) return;
    setInlineSectionSaving(true);
    try {
      const questionsInSection = questionBankExtractPreview.filter(
        (q) => (q.section_title || '') === inlineSectionEdit.sectionTitle
      );
      await Promise.all(
        questionsInSection.map((q) =>
          api.patch(`admin/question-bank/${q.id}`, {
            section_title: inlineSectionEdit.newTitle.trim() || null,
            section_instructions: inlineSectionEdit.newInstructions.trim() || null,
          })
        )
      );
      setQuestionBankExtractPreview((prev) =>
        prev.map((q) =>
          (q.section_title || '') === inlineSectionEdit.sectionTitle
            ? { ...q, section_title: inlineSectionEdit.newTitle.trim() || null, section_instructions: inlineSectionEdit.newInstructions.trim() || null }
            : q
        )
      );
      setInlineSectionEdit(null);
      toast.success('Section updated');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to update section');
    } finally {
      setInlineSectionSaving(false);
    }
  };

  const removeInlineSectionBreak = async () => {
    if (!inlineSectionEdit) return;
    setInlineSectionSaving(true);
    try {
      const questionsInSection = questionBankExtractPreview.filter(
        (q) => (q.section_title || '') === inlineSectionEdit.sectionTitle
      );
      await Promise.all(
        questionsInSection.map((q) =>
          api.patch(`admin/question-bank/${q.id}`, { section_title: null, section_instructions: null })
        )
      );
      setQuestionBankExtractPreview((prev) =>
        prev.map((q) =>
          (q.section_title || '') === inlineSectionEdit.sectionTitle
            ? { ...q, section_title: null, section_instructions: null }
            : q
        )
      );
      setInlineSectionEdit(null);
      toast.success('Section break removed');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to remove section break');
    } finally {
      setInlineSectionSaving(false);
    }
  };

  const dndSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const syncExtractOrder = async (next) => {
    if (!editingPaper) return;
    setQuestionBankExtractPreview(next);
    try {
      await api.post(`admin/paper/${editingPaper.id}/sync-question-bank-extract`, {
        question_bank_question_ids: next.map((q) => q.id),
      });
    } catch (e) {
      setQuestionBankExtractPreview(questionBankExtractPreview);
      toast.error('Failed to reorder questions');
    }
  };

  const syncSubQuestionOrder = async (parentQ, newSubs) => {
    setQuestionBankExtractPreview((prev) =>
      prev.map((q) => q.id === parentQ.id ? { ...q, sub_questions: newSubs } : q)
    );
    try {
      await api.patch(`admin/question-bank/${parentQ.id}/reorder-sub-questions`, {
        child_ids: newSubs.map((s) => s.id),
      });
    } catch (e) {
      setQuestionBankExtractPreview((prev) =>
        prev.map((q) => q.id === parentQ.id ? { ...q, sub_questions: parentQ.sub_questions } : q)
      );
      toast.error('Failed to reorder sub-questions');
    }
  };

  const handleRenumber = async () => {
    if (!editingPaper) return;
    const next = questionBankExtractPreview.map((q, idx) => ({ ...q, paper_number: String(idx + 1) }));
    setQuestionBankExtractPreview(next);
    setEditingPaperNumbers({});
    try {
      await Promise.all(
        questionBankExtractPreview.map((q, idx) =>
          String(q.paper_number ?? '') !== String(idx + 1)
            ? api.patch(`admin/question-bank/${q.id}`, { paper_number: String(idx + 1) })
            : Promise.resolve()
        )
      );
      toast.success(`Questions renumbered 1–${next.length}`);
    } catch (e) {
      toast.error('Renumber failed for some questions');
      try {
        const res = await api.get(`admin/paper/${editingPaper.id}`);
        setQuestionBankExtractPreview(res.data?.data?.question_bank_extract_questions || []);
      } catch (_) { /* ignore */ }
    }
  };

  const handleAddQuestionSave = async (payload) => {
    if (!editingPaper) return;
    setAddQSaving(true);
    try {
      const { mode, targetIdx, parentQ } = addQDialog;

      if (mode === 'sub-question' && parentQ) {
        payload.parent_question_id = parentQ.id;
        payload.sort_order = (parentQ.sub_questions || []).length;
      }

      const res = await api.post('admin/question-bank', payload);
      const newQ = res.data?.data;
      if (!newQ?.id) throw new Error('No question ID returned');

      if (mode === 'sub-question') {
        const parentRes = await api.get(`admin/question-bank/${parentQ.id}`);
        const updatedSubs = parentRes.data?.data?.sub_questions || [];
        setQuestionBankExtractPreview((prev) =>
          prev.map((q) => q.id === parentQ.id ? { ...q, sub_questions: updatedSubs } : q)
        );
      } else {
        const currentIds = questionBankExtractPreview.map((q) => q.id);
        const insertAt = mode === 'before' ? targetIdx : targetIdx + 1;
        const newIds = [...currentIds.slice(0, insertAt), newQ.id, ...currentIds.slice(insertAt)];
        await api.post(`admin/paper/${editingPaper.id}/sync-question-bank-extract`, {
          question_bank_question_ids: newIds,
        });
        const paperRes = await api.get(`admin/paper/${editingPaper.id}`);
        setQuestionBankExtractPreview(paperRes.data?.data?.question_bank_extract_questions || []);
      }

      toast.success('Question created and added to paper');
      setAddQDialog({ open: false, mode: null, targetIdx: null, parentQ: null });
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to create question');
    } finally {
      setAddQSaving(false);
    }
  };

  const moveExtractQuestion = (idx, direction) => {
    const next = [...questionBankExtractPreview];
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= next.length) return;
    [next[idx], next[swapIdx]] = [next[swapIdx], next[idx]];
    syncExtractOrder(next);
  };

  const handleExtractDragEnd = ({ active, over }) => {
    if (!active?.id || !over?.id || String(active.id) === String(over.id)) return;
    const ids = questionBankExtractPreview.map((q) => String(q.id));
    const oldIndex = ids.indexOf(String(active.id));
    const newIndex = ids.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    syncExtractOrder(arrayMove(questionBankExtractPreview, oldIndex, newIndex));
  };

  const saveQSectionEdit = async () => {
    if (!qSectionEdit) return;
    setQSectionSaving(true);
    try {
      await api.patch(`admin/question-bank/${qSectionEdit.qId}`, {
        section_title: qSectionEdit.title.trim() || null,
        section_instructions: qSectionEdit.instructions.trim() || null,
      });
      setQuestionBankExtractPreview((prev) =>
        prev.map((q) =>
          q.id === qSectionEdit.qId
            ? { ...q, section_title: qSectionEdit.title.trim() || null, section_instructions: qSectionEdit.instructions.trim() || null }
            : q
        )
      );
      setQSectionEdit(null);
      toast.success('Section updated');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to update section');
    } finally {
      setQSectionSaving(false);
    }
  };

  const removeQSectionBreak = async (qId) => {
    setQSectionSaving(true);
    try {
      await api.patch(`admin/question-bank/${qId}`, { section_title: null, section_instructions: null });
      setQuestionBankExtractPreview((prev) =>
        prev.map((q) => q.id === qId ? { ...q, section_title: null, section_instructions: null } : q)
      );
      setQSectionEdit(null);
      toast.success('Section break removed');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to remove section break');
    } finally {
      setQSectionSaving(false);
    }
  };

  const handleScaleTo100 = async () => {
    const getQMarks = (q) => {
      if (q.type === 'grouped' && Array.isArray(q.sub_questions) && q.sub_questions.length > 0) {
        const s = q.sub_questions.reduce((acc, sq) => acc + (parseInt(sq.default_marks, 10) || 0), 0);
        return s > 0 ? s : (parseInt(q.default_marks, 10) || 1);
      }
      return parseInt(q.default_marks, 10) || 1;
    };

    const total = questionBankExtractPreview.reduce((s, q) => s + getQMarks(q), 0);
    if (total === 100) { toast.info('Already at 100 marks'); return; }
    if (total === 0) { toast.error('No marks assigned yet'); return; }

    // Largest-remainder algorithm so scaled values sum to exactly 100
    const rawMarks = questionBankExtractPreview.map(q => getQMarks(q) * 100 / total);
    const floored = rawMarks.map(v => Math.max(1, Math.floor(v)));
    const deficit = 100 - floored.reduce((s, v) => s + v, 0);
    const order = rawMarks
      .map((v, i) => ({ i, frac: v - Math.floor(v) }))
      .sort((a, b) => b.frac - a.frac);
    for (let k = 0; k < deficit; k++) floored[order[k].i]++;

    setScalingMarks(true);
    try {
      const next = [...questionBankExtractPreview];
      for (let idx = 0; idx < next.length; idx++) {
        const q = next[idx];
        const newTotal = floored[idx];
        if (q.type === 'grouped' && Array.isArray(q.sub_questions) && q.sub_questions.length > 0) {
          const subTotal = q.sub_questions.reduce((s, sq) => s + (parseInt(sq.default_marks, 10) || 0), 0);
          const newSubs = [...q.sub_questions];
          if (subTotal > 0) {
            const rawSub = newSubs.map(sq => (parseInt(sq.default_marks, 10) || 0) * newTotal / subTotal);
            const flooredSub = rawSub.map(v => Math.max(1, Math.floor(v)));
            const subDeficit = newTotal - flooredSub.reduce((s, v) => s + v, 0);
            const subOrder = rawSub.map((v, i) => ({ i, frac: v - Math.floor(v) })).sort((a, b) => b.frac - a.frac);
            for (let k = 0; k < Math.max(0, subDeficit); k++) flooredSub[subOrder[k].i]++;
            for (let si = 0; si < newSubs.length; si++) {
              if (newSubs[si].default_marks !== flooredSub[si]) {
                await api.put(`admin/question-bank/${newSubs[si].id}`, { default_marks: flooredSub[si] });
                newSubs[si] = { ...newSubs[si], default_marks: flooredSub[si] };
              }
            }
          }
          await api.put(`admin/question-bank/${q.id}`, { default_marks: newTotal });
          next[idx] = { ...q, default_marks: newTotal, sub_questions: newSubs };
        } else {
          if (q.default_marks !== newTotal) {
            await api.put(`admin/question-bank/${q.id}`, { default_marks: newTotal });
          }
          next[idx] = { ...q, default_marks: newTotal };
        }
      }
      setQuestionBankExtractPreview(next);
      toast.success('Marks scaled to 100 total');
      if (canViewPapers) loadPapers(appliedFilters, page, rowsPerPage);
      if (viewDialog && viewingPaper) {
        try {
          const vRes = await api.get(`admin/paper/${viewingPaper.id}`);
          if (vRes.data?.data) setViewingPaper(vRes.data.data);
        } catch (_) {}
      }
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to scale marks');
    } finally {
      setScalingMarks(false);
    }
  };

  const saveAssignQuestionBank = async () => {
    if (!assignQbPaper || assignQbSelected.length === 0) {
      toast.error('Select at least one question from the question bank');
      return;
    }
    setAssignQbSaving(true);
    try {
      await api.post(`admin/paper/${assignQbPaper.id}/sync-question-bank-extract`, {
        question_bank_question_ids: assignQbSelected.map((q) => q.id),
      });
      toast.success('Paper extract questions updated');
      const savedPaperId = assignQbPaper.id;
      setAssignQbOpen(false);
      setAssignQbPaper(null);
      setAssignQbSelected([]);
      loadPapers(appliedFilters, page, rowsPerPage);
      if (editingPaper && editingPaper.id === savedPaperId) {
        try {
          const res = await api.get(`admin/paper/${savedPaperId}`);
          const d = res.data?.data;
          setQuestionBankExtractPreview(Array.isArray(d?.question_bank_extract_questions) ? d.question_bank_extract_questions : []);
        } catch (_) {
          /* ignore */
        }
      }
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to save');
    } finally {
      setAssignQbSaving(false);
    }
  };

  // Cleanup batch poll on unmount
  useEffect(() => () => clearInterval(batchPollRef.current), []);

  useEffect(() => {
    if (canViewPapers) {
      loadPapers(appliedFilters, page, rowsPerPage);
    }
    fetchCategories();
    fetchFormats();
    fetchSubjects();
    fetchSchoolYears();
    fetchSchools();
  }, [canViewPapers]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!canViewPapers && showPaperFormTab) {
      setTabValue(1);
    }
  }, [canViewPapers, showPaperFormTab]);

  // Allow deep-linking directly to create tab: /admin/papers?tab=create
  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const tab = String(params.get('tab') || '').toLowerCase();
    if (tab !== 'create') return;

    if (showPaperFormTab && (canCreatePapers || canUpdatePapers)) {
      setTabValue(1);
    }
  }, [location.search, showPaperFormTab, canCreatePapers, canUpdatePapers]);

  useEffect(() => {
    if (tabValue === 1 && !canCreatePapers && !editingPaper && canViewPapers) {
      setTabValue(0);
    }
  }, [tabValue, canCreatePapers, editingPaper, canViewPapers]);

  const handlePaperTabChange = (e, newValue) => {
    if (!canViewPapers || !hasCreatePaperTab) return;
    if (newValue === 1 && !canCreatePapers && !editingPaper) {
      toast.info('Select a paper to edit from the list, or you need permission to create papers.');
      return;
    }
    setTabValue(newValue);
  };

  // Submit handler
  const onSubmit = async (data) => {
    if (isSubmitting) return;

    if (editingPaper && !canUpdatePapers) {
      toast.error('You do not have permission to update papers.');
      return;
    }
    if (!editingPaper && !canCreatePapers) {
      toast.error('You do not have permission to create papers.');
      return;
    }

    const validQuestions = questions.filter(q => plainTextLength(q.question_text || '') >= 1);
    const hasValidOptions = validQuestions.every(q => {
      const opts = (q.options || []).filter(o => (o || '').toString().trim());
      return opts.length >= 2 && (q.correct_answer || '').trim() && opts.includes(q.correct_answer);
    });
    const hasExtractQuestions = editingPaper && questionBankExtractPreview.length > 0;
    if (validQuestions.length === 0 && !hasExtractQuestions) {
      toast.error('Add at least one question with text (not empty), or assign questions from the question bank');
      return;
    }
    if (validQuestions.length > 0 && !hasValidOptions) {
      toast.error('Each question must have at least 2 options and a selected correct answer');
      return;
    }

    setIsSubmitting(true);

    try {
      const formData = new FormData();
      
      formData.append('name', data.name);
      if (data.description) formData.append('description', data.description);
      if (data.original_paper_link !== undefined) {
        formData.append('original_paper_link', (data.original_paper_link || '').trim());
      }
      formData.append('category_id', Number(data.category_id));
      formData.append('format_id', Number(data.format));
      formData.append('school_year_id', Number(data.school_year_id));
      (data.subject_ids || []).forEach((id) => {
        if (id !== '' && id != null && !Number.isNaN(Number(id))) {
          formData.append('subject_ids[]', Number(id));
        }
      });
      (data.target_school_ids || []).forEach((id) => formData.append('target_school_ids[]', id));
      formData.append('price', data.price);
      if (data.currency) formData.append('currency', data.currency);
      if (data.due_at) formData.append('due_at', new Date(data.due_at).toISOString());
      else formData.append('due_at', '');
      if (data.duration_minutes != null && data.duration_minutes !== '') {
        formData.append('duration_minutes', data.duration_minutes);
      } else {
        formData.append('duration_minutes', '');
      }
      const priceNum = parseFloat(String(data.price ?? "").replace(",", "."));
      const effectiveFree =
        !!data.is_free_paper || (!Number.isNaN(priceNum) && priceNum <= 0);
      formData.append("is_free_paper", effectiveFree ? "1" : "0");
      
      // Handle image - either new file or removal flag
      if (data.paper_image && data.paper_image instanceof File) {
        formData.append('paper_image', data.paper_image);
      } else if (removeImage && editingPaper) {
        formData.append('paper_image', '');
      }

      // Questions (manual create paper) — only when there are valid manual rows (extract-only updates skip this)
      const toSend = questions.filter(q => plainTextLength(q.question_text || '') >= 1);
      toSend.forEach((q, i) => {
        formData.append(`questions[${i}]`, q.question_text || '');
        (q.options || []).filter(o => (o || '').toString().trim()).forEach((opt, j) => formData.append(`options[${i}][${j}]`, opt.toString().trim()));
        formData.append(`answers[${i}]`, (q.correct_answer || '').trim());
        formData.append(`duration_in_sec[${i}]`, q.duration_in_sec ?? 60);
        formData.append(`marks[${i}]`, q.marks ?? 1);
        formData.append(`is_free[${i}]`, q.is_free ? '1' : '0');
        (q.topic_ids || []).forEach((tid) => {
          formData.append(`topic_ids[${i}][]`, tid);
        });
      });

      if (editingPaper) {
        formData.append('_method', 'PUT');
        const response = await api.post(`admin/paper/${editingPaper.id}`, formData);
        toast.success(response.data.message || 'Paper updated successfully');
      } else {
        const response = await api.post('admin/paper', formData);
        toast.success(response.data.message || 'Paper created successfully');
      }
      
      reset();
      setQuestions([{ question_text: '', options: ['', ''], correct_answer: '', marks: 1, duration_in_sec: 60, is_free: false, topic_ids: [] }]);
      setQuestionBankExtractPreview([]);
      setEditingBankQuestion(null);
      setExistingImageUrl(null);
      setFormSourceLinks([]);
      setRemoveImage(false);
      setEditingPaper(null);
      setTabValue(canViewPapers ? 0 : 1);
      if (canViewPapers) {
        setPage(0);
        loadPapers(appliedFilters, 0, rowsPerPage);
      }
      
    } catch (error) {
      console.error('Error submitting paper:', error);
      const errorMsg = error.response?.data?.message || 'Failed to submit paper';
      toast.error(errorMsg);
      
      if (error.response?.data?.errors) {
        const validationErrors = error.response.data.errors;
        Object.keys(validationErrors).forEach(key => {
          toast.error(`${key}: ${validationErrors[key].join(', ')}`);
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleView = async (paper) => {
    if (!canViewPapers) {
      toast.error('You do not have permission to view papers.');
      return;
    }
    try {
      const response = await api.get(`admin/paper/${paper.id}`);
      setViewingPaper(response.data.data);
      setViewDialog(true);
    } catch (error) {
      console.error('Error fetching paper details:', error);
      toast.error('Failed to load paper details');
    }
  };

  const handleViewPdf = async (paper) => {
    if (!canViewPapers) {
      toast.error('You do not have permission to view papers.');
      return;
    }
    setViewingPdfPaperId(paper.id);
    try {
      const response = await api.get(`admin/paper/${paper.id}/view-pdf`, { responseType: 'blob' });
      const contentType = (response.headers?.['content-type'] || '').toLowerCase();
      if (!contentType.includes('application/pdf')) {
        let message = 'No PDF is available for this paper yet.';
        try {
          const text = await response.data.text();
          const parsed = JSON.parse(text);
          if (parsed?.message) message = parsed.message;
        } catch (_) {}
        toast.error(message);
        return;
      }
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to open paper PDF');
    } finally {
      setViewingPdfPaperId(null);
    }
  };

  const handleDownloadAnswerKey = async (paper) => {
    if (!canViewPapers) {
      toast.error('You do not have permission to download answer keys.');
      return;
    }
    setDownloadingAnswerKeyPaperId(paper.id);
    try {
      const response = await api.get(`admin/paper/${paper.id}/download-answer-key`, { responseType: 'blob' });
      const contentType = (response.headers?.['content-type'] || '').toLowerCase();
      if (!contentType.includes('application/pdf')) {
        let message = 'Failed to download answer key.';
        try {
          const text = await response.data.text();
          const parsed = JSON.parse(text);
          if (parsed?.message) message = parsed.message;
        } catch (_) {}
        toast.error(message);
        return;
      }
      const safeName = (paper?.name || `paper-${paper.id}`)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${safeName || `paper-${paper.id}`}-answer-key.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Answer key download started');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to download answer key');
    } finally {
      setDownloadingAnswerKeyPaperId(null);
    }
  };

  const handleEdit = async (paper) => {
    if (!canUpdatePapers) {
      toast.error('You do not have permission to edit papers.');
      return;
    }
    try {
      const response = await api.get(`admin/paper/${paper.id}`);
      const paperData = response.data.data;
      
      reset({
        name: paperData.name || '',
        description: paperData.description || null,
        original_paper_link: paperData.original_paper_link || '',
        category_id: paperData.category_id || '',
        format: paperData.format_id || '',
        school_year_id: paperData.school_year_id != null && paperData.school_year_id !== '' ? Number(paperData.school_year_id) : '',
        subject_ids: Array.isArray(paperData.subject_ids)
          ? paperData.subject_ids.map((id) => Number(id))
          : Array.isArray(paperData.subjects) && paperData.subjects.length && typeof paperData.subjects[0] === 'object'
            ? paperData.subjects.map((s) => Number(s.id))
            : [],
        target_school_ids: Array.isArray(paperData.target_school_ids) ? paperData.target_school_ids.map(String) : [],
        price: paperData.price ?? "",
        currency: paperData.currency || "£",
        paper_image: null,
        is_free_paper:
          !!paperData.is_free_paper ||
          (!Number.isNaN(parseFloat(String(paperData.price ?? "").replace(",", "."))) &&
            parseFloat(String(paperData.price ?? "").replace(",", ".")) <= 0),
        due_at: paperData.due_at ? paperData.due_at.slice(0, 16) : '',
        duration_minutes: paperData.duration_minutes != null ? paperData.duration_minutes : '',
      });

      const loadedQuestions = (paperData.questions || []).length > 0
        ? paperData.questions.map(q => ({
            question_text: q.question_text || '',
            options: (q.options || []).map(o => o.option_text || ''),
            correct_answer: q.correct_answer || '',
            marks: q.marks ?? 1,
            duration_in_sec: q.duration_in_sec ?? 60,
            is_free: !!q.is_free,
            topic_ids: Array.isArray(q.topic_ids) ? q.topic_ids.map((id) => Number(id)).filter((n) => !Number.isNaN(n)) : []
          }))
        : [{ question_text: '', options: ['', ''], correct_answer: '', marks: 1, duration_in_sec: 60, is_free: false, topic_ids: [] }];
      setQuestions(loadedQuestions);

      const qbEx = paperData.question_bank_extract_questions;
      setQuestionBankExtractPreview(Array.isArray(qbEx) ? qbEx : []);

      setEditingPaper(paperData);
      setExistingImageUrl(paperData.image);
      setFormSourceLinks(Array.isArray(paperData.source_links) ? paperData.source_links : []);
      setRemoveImage(false);
      setTabValue(1);
      
      toast.info('Edit mode: Update any fields you want to change');
    } catch (error) {
      console.error('Error loading paper for edit:', error);
      toast.error('Failed to load paper data');
    }
  };

  const handleDelete = (paper) => {
    if (!canDeletePapers) {
      toast.error('You do not have permission to delete papers.');
      return;
    }
    setDeleteDialog({ open: true, paper, deleteQuestionBankToo: false, forceDelete: false });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteDialog.paper || !canDeletePapers) return;

    if (deleteDialog.forceDelete) {
      const proceed = window.confirm(
        'WARNING: This paper has been purchased. Deleting it will revoke access for all buyers. Are you absolutely sure?'
      );
      if (!proceed) return;
    } else if (deleteDialog.deleteQuestionBankToo) {
      const proceed = window.confirm(
        'This will also delete related Question Bank questions for this paper when safe. Continue?'
      );
      if (!proceed) {
        return;
      }
    }
    
    setDeletingPaperId(deleteDialog.paper.id);
    try {
      const response = await api.delete(`admin/paper/${deleteDialog.paper.id}`, {
        data: {
          delete_linked_question_bank_questions: deleteDialog.deleteQuestionBankToo,
          force_delete: deleteDialog.forceDelete,
        },
      });
      const deletedCount = response.data?.data?.deleted_question_ids?.length || 0;
      const skippedCount = response.data?.data?.skipped_question_ids?.length || 0;
      if (deleteDialog.deleteQuestionBankToo) {
        toast.success(`Paper deleted. Question Bank cleanup: ${deletedCount} deleted, ${skippedCount} skipped.`);
      } else {
        toast.success('Paper deleted successfully');
      }
      if (canViewPapers) {
        loadPapers(appliedFilters, page, rowsPerPage);
      }
      setDeleteDialog({ open: false, paper: null, deleteQuestionBankToo: false, forceDelete: false });
    } catch (error) {
      console.error('Error deleting paper:', error);
      toast.error(error.response?.data?.message || 'Failed to delete paper');
    } finally {
      setDeletingPaperId(null);
    }
  };

  const handleToggleStatus = async (paper) => {
    if (!canUpdatePapers) {
      toast.error('You do not have permission to update papers.');
      return;
    }
    setTogglingPaperId(paper.id);
    try {
      const action = paper.status === 2 ? 'deactivate' : 'activate';
      await api.patch(`admin/paper/${paper.id}/toggle-status`, { action });
      toast.success(`Paper ${action}d successfully`);
      if (canViewPapers) {
        loadPapers(appliedFilters, page, rowsPerPage);
      }
    } catch (error) {
      console.error('Error toggling status:', error);
      toast.error('Failed to toggle status');
    } finally {
      setTogglingPaperId(null);
    }
  };

  const handleCancelEdit = () => {
    reset();
    setQuestions([{ question_text: '', options: ['', ''], correct_answer: '', marks: 1, duration_in_sec: 60, is_free: false, topic_ids: [] }]);
    setQuestionBankExtractPreview([]);
    setEditingBankQuestion(null);
    setExistingImageUrl(null);
    setFormSourceLinks([]);
    setEditingPaper(null);
    setTabValue(canViewPapers ? 0 : 1);
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      p: 4,
      pb: 8
    }}>
      {/* Animated Header */}
      <Fade in timeout={800}>
        <Box sx={{ mb: 5 }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2, 
            mb: 2,
            animation: 'slideInDown 0.6s ease-out'
          }}>
            <Avatar sx={{ 
              width: 70, 
              height: 70, 
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              boxShadow: '0 8px 32px rgba(245, 87, 108, 0.4)'
            }}>
              <DescriptionIcon sx={{ fontSize: 40 }} />
            </Avatar>
            <Box>
              <Typography variant="h3" sx={{ 
                fontWeight: 800, 
                color: 'white',
                textShadow: '2px 2px 8px rgba(0,0,0,0.3)',
                letterSpacing: '-0.5px'
              }}>
                Papers Management
              </Typography>
              <Typography variant="body1" sx={{ 
                color: 'rgba(255,255,255,0.9)', 
                fontSize: '16px',
                fontWeight: 500
              }}>
                Create papers by adding questions one by one
              </Typography>
            </Box>
          </Box>
        </Box>
      </Fade>

      {/* Enhanced Tabs Card — list + create tab only when user can view list and create new */}
      {canViewPapers && hasCreatePaperTab ? (
        <Zoom in timeout={600}>
          <Card sx={{ 
            mb: 4,
            borderRadius: 4,
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            background: 'rgba(255,255,255,0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255,255,255,0.5)',
            overflow: 'hidden'
          }}>
            <Tabs 
              value={tabValue} 
              onChange={handlePaperTabChange}
              sx={{
                '& .MuiTab-root': {
                  fontSize: { xs: '13px', sm: '16px' },
                  fontWeight: 600,
                  textTransform: 'none',
                  minHeight: { xs: 48, sm: 70 },
                  px: { xs: 2, sm: 4 },
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: alpha('#667eea', 0.08)
                  }
                },
                '& .Mui-selected': {
                  color: '#667eea !important',
                  background: alpha('#667eea', 0.1)
                },
                '& .MuiTabs-indicator': {
                  height: 4,
                  borderRadius: '4px 4px 0 0',
                  background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)'
                }
              }}
            >
              <Tab 
                icon={<DescriptionIcon />} 
                iconPosition="start" 
                label="All Papers" 
              />
              <Tab 
                icon={editingPaper ? <EditIcon /> : <AddIcon />} 
                iconPosition="start" 
                label={editingPaper ? "Edit Paper" : "Create Paper"} 
              />
            </Tabs>
          </Card>
        </Zoom>
      ) : null}

      {/* Tab Content */}
      {tabValue === 0 && canViewPapers && (
        <Fade in timeout={600}>
          <MuiPaper sx={{ 
            p: 4, 
            borderRadius: 4,
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            background: 'rgba(255,255,255,0.98)',
            backdropFilter: 'blur(20px)'
          }}>
            {/* Fancy Filters */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ 
                mb: 3, 
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                color: '#667eea'
              }}>
                <FilterListIcon />
                Filter and search papers
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Search"
                    placeholder="Paper name or description…"
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleApplyListFilters();
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        '& fieldset': { borderWidth: 2 },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={filters.category_id}
                      onChange={(e) => setFilters({ ...filters, category_id: e.target.value })}
                      label="Category"
                      sx={{
                        borderRadius: 2,
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderWidth: 2
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#667eea'
                        }
                      }}
                    >
                      <MenuItem value="">All Categories</MenuItem>
                      {categories.map(cat => (
                        <MenuItem key={cat.id} value={cat.id}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CategoryIcon fontSize="small" color="primary" />
                            {cat.name}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Format</InputLabel>
                    <Select
                      value={filters.format}
                      onChange={(e) => setFilters({ ...filters, format: e.target.value })}
                      label="Format"
                      sx={{
                        borderRadius: 2,
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderWidth: 2
                        }
                      }}
                    >
                      <MenuItem value="">All Formats</MenuItem>
                      {formats.map(fmt => (
                        <MenuItem key={fmt.id} value={fmt.id}>{fmt.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>School year</InputLabel>
                    <Select
                      value={filters.school_year_id}
                      onChange={(e) => setFilters({ ...filters, school_year_id: e.target.value })}
                      label="School year"
                      sx={{
                        borderRadius: 2,
                        '& .MuiOutlinedInput-notchedOutline': { borderWidth: 2 },
                      }}
                    >
                      <MenuItem value="">All years</MenuItem>
                      {schoolYears.map((sy) => (
                        <MenuItem key={sy.id} value={sy.id}>{sy.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Subject</InputLabel>
                    <Select
                      value={filters.subject_id}
                      onChange={(e) => setFilters({ ...filters, subject_id: e.target.value })}
                      label="Subject"
                      sx={{
                        borderRadius: 2,
                        '& .MuiOutlinedInput-notchedOutline': { borderWidth: 2 },
                      }}
                    >
                      <MenuItem value="">All subjects</MenuItem>
                      {subjectsList.map((s) => (
                        <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>School</InputLabel>
                    <Select
                      value={filters.school_id}
                      onChange={(e) => setFilters({ ...filters, school_id: e.target.value })}
                      label="School"
                      sx={{
                        borderRadius: 2,
                        '& .MuiOutlinedInput-notchedOutline': { borderWidth: 2 },
                      }}
                    >
                      <MenuItem value="">All schools</MenuItem>
                      {schools.map((sch) => (
                        <MenuItem key={sch.id} value={sch.id}>{sch.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={filters.status}
                      onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                      label="Status"
                      sx={{
                        borderRadius: 2,
                        '& .MuiOutlinedInput-notchedOutline': { borderWidth: 2 },
                      }}
                    >
                      <MenuItem value="">All statuses</MenuItem>
                      <MenuItem value="2">Active</MenuItem>
                      <MenuItem value="3">Inactive</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Source link</InputLabel>
                    <Select
                      value={filters.has_source_link}
                      onChange={(e) => setFilters({ ...filters, has_source_link: e.target.value })}
                      label="Source link"
                      sx={{
                        borderRadius: 2,
                        '& .MuiOutlinedInput-notchedOutline': { borderWidth: 2 },
                      }}
                    >
                      <MenuItem value="">All papers</MenuItem>
                      <MenuItem value="yes">Has source link</MenuItem>
                      <MenuItem value="no">No source link</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={handleClearListFilters}
                    startIcon={<ClearIcon />}
                    sx={{
                      height: '56px',
                      borderRadius: 2,
                      borderWidth: 2,
                      borderColor: '#667eea',
                      color: '#667eea',
                      fontWeight: 600,
                      textTransform: 'none',
                      fontSize: '1rem',
                      '&:hover': {
                        borderWidth: 2,
                        borderColor: '#764ba2',
                        backgroundColor: 'rgba(102, 126, 234, 0.08)',
                      },
                    }}
                  >
                    Clear filters
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6} md={8}>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={handleApplyListFilters}
                    startIcon={<SearchIcon />}
                    sx={{ 
                      height: '56px',
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      fontSize: '16px',
                      fontWeight: 700,
                      textTransform: 'none',
                      boxShadow: '0 8px 24px rgba(102, 126, 234, 0.4)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 12px 32px rgba(102, 126, 234, 0.5)',
                        background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)'
                      }
                    }}
                  >
                    Apply filters
                  </Button>
                </Grid>
              </Grid>
            </Box>

            <Divider sx={{ my: 4 }} />

            {/* Batch source-link progress banner */}
            {batchLinkStatus && batchLinkStatus.status !== 'idle' && (
              <Alert
                severity={
                  batchLinkStatus.status === 'failed' ? 'error' :
                  batchLinkStatus.status === 'completed' ? 'success' : 'info'
                }
                sx={{ mb: 2 }}
                onClose={() => setBatchLinkStatus(null)}
              >
                {batchLinkStatus.status === 'processing' && (
                  <>Finding source links… {batchLinkStatus.processed}/{batchLinkStatus.total} processed, {batchLinkStatus.updated} updated</>
                )}
                {batchLinkStatus.status === 'completed' && (
                  <>Batch complete — {batchLinkStatus.updated} of {batchLinkStatus.total} papers updated with source links.</>
                )}
                {batchLinkStatus.status === 'failed' && `Batch failed: ${batchLinkStatus.error || 'Unknown error'}`}
                {batchLinkStatus.status === 'pending' && 'Batch job queued — processing will begin shortly…'}
              </Alert>
            )}

            {/* Batch "Find All Missing Source Links" button */}
            {canUpdatePapers && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={batchLinkRunning ? <CircularProgress size={16} /> : <TravelExploreIcon />}
                  disabled={batchLinkRunning}
                  onClick={handleBatchFindLinks}
                  sx={{ textTransform: 'none', borderRadius: 2 }}
                >
                  Find All Missing Source Links
                </Button>
              </Box>
            )}

            {/* Fancy Table */}
            <TableContainer sx={{ 
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider'
            }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ 
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                  }}>
                    <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '15px' }}>Image</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '15px' }}>Name</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '15px' }}>Category</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '15px' }}>Format</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '15px' }}>School year</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '15px' }}>Subjects</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '15px' }}>Price</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '15px' }}>Questions</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '15px' }}>Total Marks</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '15px' }}>Status</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '15px' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    [...Array(5)].map((_, i) => (
                      <TableRow key={i}>
                        <TableCell colSpan={11}><Skeleton height={60} /></TableCell>
                      </TableRow>
                    ))
                  ) : papers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={11}>
                        <Box sx={{ 
                          textAlign: 'center', 
                          py: 8,
                          color: 'text.secondary'
                        }}>
                          <DescriptionIcon sx={{ fontSize: 80, opacity: 0.3, mb: 2 }} />
                          <Typography variant="h6" fontWeight={600}>No papers found</Typography>
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            Create your first paper to get started
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    papers.map((paper, index) => (
                      <Zoom in key={paper.id} timeout={300 + index * 50}>
                        <TableRow sx={{ 
                          '&:hover': {
                            background: alpha('#667eea', 0.05),
                            transform: 'scale(1.01)',
                            transition: 'all 0.2s ease'
                          }
                        }}>
                          <TableCell>
                            {paper.image ? (
                              <Tooltip title="Click to preview" arrow>
                                <Box 
                                  onClick={() => handleImagePreview(paper.image)}
                                  sx={{ 
                                    cursor: 'pointer',
                                    position: 'relative',
                                    width: 60,
                                    height: 60,
                                    borderRadius: 2,
                                    overflow: 'hidden',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                      transform: 'scale(1.05)',
                                      boxShadow: '0 6px 20px rgba(0,0,0,0.25)'
                                    }
                                  }}
                                >
                                  <img 
                                    src={paper.image} 
                                    alt={paper.name}
                                    style={{
                                      width: '100%',
                                      height: '100%',
                                      objectFit: 'cover'
                                    }}
                                  />
                                </Box>
                              </Tooltip>
                            ) : (
                              <Avatar sx={{ 
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                width: 60,
                                height: 60
                              }}>
                                <DescriptionIcon />
                              </Avatar>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography fontWeight={600}>{paper.name}</Typography>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              icon={<CategoryIcon />}
                              label={paper.category} 
                              size="small"
                              sx={{ 
                                fontWeight: 600,
                                background: alpha('#667eea', 0.1),
                                color: '#667eea',
                                border: '1px solid',
                                borderColor: alpha('#667eea', 0.3)
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography fontWeight={500}>{paper.format}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>
                              {paper.school_year || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {Array.isArray(paper.subjects) && paper.subjects.length > 0 ? (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: 280 }}>
                                {paper.subjects.map((name, idx) => (
                                  <Chip
                                    key={`${name}-${idx}`}
                                    label={name}
                                    size="small"
                                    sx={{ fontWeight: 600, background: alpha('#7c3aed', 0.12), color: '#5b21b6' }}
                                  />
                                ))}
                              </Box>
                            ) : paper.subject ? (
                              <Chip label={paper.subject} size="small" sx={{ fontWeight: 600 }} />
                            ) : (
                              <Typography variant="body2" color="text.secondary">—</Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ 
                              display: 'inline-flex', 
                              alignItems: 'center', 
                              gap: 0.5,
                              background: alpha('#4caf50', 0.1),
                              px: 2,
                              py: 0.5,
                              borderRadius: 2,
                              border: '1px solid',
                              borderColor: alpha('#4caf50', 0.3)
                            }}>
                              <Typography fontWeight={700} color="#4caf50">
                                {paper.currency || '£'}{paper.price}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={`${
                                (paper.extract_questions_count ?? 0) > 0
                                  ? paper.extract_questions_count
                                  : (paper.questions_count ?? paper.pdfs_count ?? 0)
                              } Q`}
                              size="small"
                              sx={{ fontWeight: 600, background: alpha('#667eea', 0.1), color: '#667eea' }}
                            />
                          </TableCell>
                          <TableCell>
                            {(() => {
                              const declared = paper.total_marks ?? 0;
                              const assigned = paper.extract_marks_total ?? 0;
                              if (!declared && !assigned) {
                                return <Typography variant="body2" color="text.secondary">—</Typography>;
                              }
                              const mismatch = assigned > 0 && declared > 0 && assigned !== declared;
                              return (
                                <Tooltip
                                  title={mismatch ? `${assigned} marks assigned · ${declared} declared total` : ''}
                                  arrow
                                  TransitionComponent={Zoom}
                                >
                                  <Chip
                                    label={mismatch ? `${assigned} / ${declared} marks` : `${declared || assigned} marks`}
                                    size="small"
                                    sx={{
                                      fontWeight: 600,
                                      background: alpha(mismatch ? '#f44336' : '#ff9800', 0.1),
                                      color: mismatch ? '#c62828' : '#e65100',
                                    }}
                                  />
                                </Tooltip>
                              );
                            })()}
                          </TableCell>
                          <TableCell>
                            <Chip
                              icon={paper.status === 2 ? <CheckIcon /> : <InfoIcon />}
                              label={paper.status === 2 ? 'Active' : 'Inactive'} 
                              size="small"
                              sx={{ 
                                fontWeight: 700,
                                ...(paper.status === 2 ? {
                                  background: 'linear-gradient(135deg, #4caf50 0%, #8bc34a 100%)',
                                  color: 'white',
                                  boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)'
                                } : {
                                  background: alpha('#9e9e9e', 0.2),
                                  color: '#616161'
                                })
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              {canViewPapers && (
                              <Tooltip title="View Details" arrow TransitionComponent={Zoom}>
                                <IconButton 
                                  onClick={() => handleView(paper)} 
                                  size="small"
                                  sx={{ 
                                    background: alpha('#2196f3', 0.1),
                                    '&:hover': {
                                      background: alpha('#2196f3', 0.2),
                                      transform: 'scale(1.1)'
                                    }
                                  }}
                                >
                                  <ViewIcon sx={{ color: '#2196f3' }} />
                                </IconButton>
                              </Tooltip>
                              )}
                              {canViewPapers && (
                              <Tooltip title="View PDF" arrow TransitionComponent={Zoom}>
                                <span>
                                  <IconButton
                                    onClick={() => handleViewPdf(paper)}
                                    size="small"
                                    disabled={viewingPdfPaperId === paper.id}
                                    sx={{
                                      background: alpha('#5c6bc0', 0.1),
                                      '&:hover': {
                                        background: alpha('#5c6bc0', 0.2),
                                        transform: 'scale(1.1)',
                                      },
                                    }}
                                  >
                                    {viewingPdfPaperId === paper.id ? (
                                      <CircularProgress size={18} sx={{ color: '#5c6bc0' }} />
                                    ) : (
                                      <PictureAsPdfIcon sx={{ color: '#5c6bc0' }} />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                              )}
                              {canViewPapers && (
                              <Tooltip
                                title={
                                  paper.has_mcq_conversion
                                    ? "This paper has already been converted to MCQs — open the review"
                                    : "Convert this paper's questions to MCQ"
                                }
                                arrow
                                TransitionComponent={Zoom}
                              >
                                <span>
                                  <IconButton
                                    onClick={() =>
                                      paper.has_mcq_conversion
                                        ? navigate(`/admin/question-bank/mcq-review?job=${paper.mcq_conversion_job_id}`)
                                        : setMcqConvertPaper(paper)
                                    }
                                    size="small"
                                    sx={{
                                      background: alpha(paper.has_mcq_conversion ? '#2e7d32' : '#7b1fa2', 0.1),
                                      '&:hover': {
                                        background: alpha(paper.has_mcq_conversion ? '#2e7d32' : '#7b1fa2', 0.2),
                                        transform: 'scale(1.1)',
                                      },
                                    }}
                                  >
                                    <QuizIcon sx={{ color: paper.has_mcq_conversion ? '#2e7d32' : '#7b1fa2' }} />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              )}
                              {canViewPapers && (
                              <Tooltip title="Download Answer Key" arrow TransitionComponent={Zoom}>
                                <span>
                                  <IconButton
                                    onClick={() => handleDownloadAnswerKey(paper)}
                                    size="small"
                                    disabled={downloadingAnswerKeyPaperId === paper.id}
                                    sx={{
                                      background: alpha('#00897b', 0.1),
                                      '&:hover': {
                                        background: alpha('#00897b', 0.2),
                                        transform: 'scale(1.1)',
                                      },
                                    }}
                                  >
                                    {downloadingAnswerKeyPaperId === paper.id ? (
                                      <CircularProgress size={18} sx={{ color: '#00897b' }} />
                                    ) : (
                                      <DownloadIcon sx={{ color: '#00897b' }} />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                              )}
                              {canUpdatePapers && (
                                <Tooltip title={paper.status === 2 ? 'Assign question bank' : 'Approve paper first'} arrow TransitionComponent={Zoom}>
                                  <span>
                                    <IconButton
                                      onClick={() => openAssignQuestionBank(paper)}
                                      size="small"
                                      disabled={paper.status !== 2}
                                      sx={{
                                        background: alpha('#3f2b96', 0.1),
                                        '&:hover': {
                                          background: alpha('#3f2b96', 0.2),
                                          transform: 'scale(1.1)',
                                        },
                                      }}
                                    >
                                      <QuizIcon sx={{ color: '#3f2b96' }} />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              )}
                              {canUpdatePapers && (
                                <Tooltip title="Edit" arrow TransitionComponent={Zoom}>
                                  <IconButton 
                                    onClick={() => handleEdit(paper)} 
                                    size="small"
                                    sx={{ 
                                      background: alpha('#ff9800', 0.1),
                                      '&:hover': {
                                        background: alpha('#ff9800', 0.2),
                                        transform: 'scale(1.1)'
                                      }
                                    }}
                                  >
                                    <EditIcon sx={{ color: '#ff9800' }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {canUpdatePapers && (
                                <Tooltip title={paper.original_paper_link ? "Refresh source link" : "Find source link"} arrow TransitionComponent={Zoom}>
                                  <IconButton
                                    onClick={() => handleFindSourceLink(paper)}
                                    size="small"
                                    disabled={findLinkLoading && findLinkPaper?.id === paper.id}
                                    sx={{
                                      background: alpha('#00bcd4', 0.1),
                                      '&:hover': { background: alpha('#00bcd4', 0.2), transform: 'scale(1.1)' },
                                    }}
                                  >
                                    {findLinkLoading && findLinkPaper?.id === paper.id
                                      ? <CircularProgress size={18} sx={{ color: '#00bcd4' }} />
                                      : <LinkIcon sx={{ color: paper.original_paper_link ? '#00bcd4' : '#90a4ae' }} />
                                    }
                                  </IconButton>
                                </Tooltip>
                              )}
                              {canUpdatePapers && (
                                <Tooltip title={paper.status === 2 ? "Deactivate" : "Activate"} arrow>
                                  <IconButton 
                                    onClick={() => handleToggleStatus(paper)} 
                                    size="small"
                                    disabled={togglingPaperId === paper.id}
                                    sx={{ 
                                      background: alpha(paper.status === 2 ? '#4caf50' : '#9e9e9e', 0.1),
                                      '&:hover': {
                                        background: alpha(paper.status === 2 ? '#4caf50' : '#9e9e9e', 0.2),
                                        transform: 'scale(1.1)'
                                      }
                                    }}
                                  >
                                    {togglingPaperId === paper.id ? (
                                      <CircularProgress size={20} />
                                    ) : paper.status === 2 ? (
                                      <ToggleOnIcon sx={{ color: '#4caf50' }} />
                                    ) : (
                                      <ToggleOffIcon sx={{ color: '#9e9e9e' }} />
                                    )}
                                  </IconButton>
                                </Tooltip>
                              )}
                              {canDeletePapers && (
                                <Tooltip title="Delete" arrow TransitionComponent={Zoom}>
                                  <IconButton 
                                    onClick={() => handleDelete(paper)} 
                                    size="small"
                                    sx={{ 
                                      background: alpha('#f44336', 0.1),
                                      '&:hover': {
                                        background: alpha('#f44336', 0.2),
                                        transform: 'scale(1.1)'
                                      }
                                    }}
                                  >
                                    <DeleteIcon sx={{ color: '#f44336' }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      </Zoom>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={totalPapers}
              page={page}
              onPageChange={handlePageChange}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleRowsPerPageChange}
              rowsPerPageOptions={[10, 25, 50, 100]}
              sx={{
                borderTop: '1px solid rgba(102, 126, 234, 0.15)',
                '& .MuiTablePagination-toolbar': { px: 2 },
              }}
            />
          </MuiPaper>
        </Fade>
      )}

      {tabValue === 1 && showPaperFormTab && (
        <Slide direction="left" in timeout={500}>
          <Box>
            {canViewPapers && !hasCreatePaperTab ? (
              <Box sx={{ mb: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleCancelEdit}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  ← Back to papers list
                </Button>
              </Box>
            ) : null}
          <MuiPaper sx={{
            p: { xs: 2, sm: 3, md: 5 },
            borderRadius: 4,
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            background: 'rgba(255,255,255,0.98)',
            backdropFilter: 'blur(20px)'
          }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 2, 
              mb: 4,
              pb: 3,
              borderBottom: '3px solid',
              borderImage: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%) 1'
            }}>
              <Avatar sx={{ 
                width: 60, 
                height: 60, 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                boxShadow: '0 8px 24px rgba(102, 126, 234, 0.4)'
              }}>
                {editingPaper ? <EditIcon sx={{ fontSize: 30 }} /> : <AddIcon sx={{ fontSize: 30 }} />}
              </Avatar>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#667eea' }}>
                  {editingPaper ? 'Edit Paper' : 'Create New Paper'}
                </Typography>
                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                  {editingPaper ? 'Update paper details and PDFs' : 'Fill in the details to create a new paper'}
                </Typography>
              </Box>
            </Box>

            {editingPaper && (
              <Alert 
                icon={<InfoIcon />}
                severity="info" 
                sx={{ 
                  mb: 4,
                  borderRadius: 2,
                  border: '2px solid',
                  borderColor: alpha('#2196f3', 0.3),
                  background: alpha('#2196f3', 0.05),
                  '& .MuiAlert-icon': {
                    color: '#2196f3'
                  }
                }}
              >
                <Typography variant="body2" fontWeight={600}>
                  Editing: <strong style={{ color: '#667eea' }}>{editingPaper.name}</strong>
                </Typography>
                <Typography variant="caption">
                  Update any fields you want to change. Leave others as is.
                </Typography>
              </Alert>
            )}

            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid container spacing={4}>
                {/* Name */}
                <Grid item xs={12}>
                  <Controller
                    name="name"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Paper Name"
                        fullWidth
                        required
                        error={!!errors.name}
                        helperText={errors.name?.message}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <DescriptionIcon color="primary" />
                            </InputAdornment>
                          )
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            '& fieldset': {
                              borderWidth: 2
                            },
                            '&:hover fieldset': {
                              borderColor: '#667eea'
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#667eea'
                            }
                          }
                        }}
                      />
                    )}
                  />
                </Grid>

                {/* Description */}
                <Grid item xs={12}>
                  <Controller
                    name="description"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Description"
                        fullWidth
                        multiline
                        rows={4}
                        error={!!errors.description}
                        helperText={errors.description?.message}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            '& fieldset': {
                              borderWidth: 2
                            }
                          }
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="original_paper_link"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Original Paper Link"
                        fullWidth
                        placeholder="https://..."
                        error={!!errors.original_paper_link}
                        helperText={errors.original_paper_link?.message || 'Optional source URL of the original paper'}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            '& fieldset': {
                              borderWidth: 2
                            }
                          }
                        }}
                      />
                    )}
                  />
                  {formSourceLinks.length > 0 && (
                    <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 2, border: '1px solid', borderColor: alpha('#00bcd4', 0.3), background: alpha('#00bcd4', 0.04) }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                        <LinkIcon sx={{ fontSize: 16, color: '#00bcd4' }} /> Source links ({formSourceLinks.length}) · read-only
                      </Typography>
                      <List dense disablePadding>
                        {[...formSourceLinks]
                          .sort((a, b) => (b?.is_official ? 1 : 0) - (a?.is_official ? 1 : 0))
                          .map((l, i) => {
                            const url = typeof l === 'string' ? l : l.url;
                            const title = typeof l === 'string' ? l : (l.title || l.url);
                            const isOfficial = typeof l !== 'string' && !!l.is_official;
                            return (
                              <ListItem key={i} disableGutters sx={{ py: 0.25 }}>
                                <ListItemText
                                  primary={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                      <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2', wordBreak: 'break-all', fontSize: '0.8rem' }}>
                                        {title}
                                      </a>
                                      {isOfficial && (
                                        <Chip label="Official source" size="small" color="success" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700 }} />
                                      )}
                                    </Box>
                                  }
                                  secondary={title !== url ? url : null}
                                  secondaryTypographyProps={{ sx: { wordBreak: 'break-all', fontSize: '0.68rem' } }}
                                />
                              </ListItem>
                            );
                          })}
                      </List>
                    </Box>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="due_at"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Submission deadline"
                        type="datetime-local"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        error={!!errors.due_at}
                        helperText={errors.due_at?.message || 'Reminder sent 2 days before this date'}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            '& fieldset': { borderWidth: 2 },
                          },
                        }}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Controller
                    name="duration_minutes"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Duration (minutes)"
                        type="number"
                        fullWidth
                        inputProps={{ min: 1 }}
                        error={!!errors.duration_minutes}
                        helperText={errors.duration_minutes?.message || 'Total time allowed to complete the paper'}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            '& fieldset': { borderWidth: 2 },
                          },
                        }}
                      />
                    )}
                  />
                </Grid>

                {/* Category & Format */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth error={!!errors.category_id}>
                    <InputLabel>Category *</InputLabel>
                    <Controller
                      name="category_id"
                      control={control}
                      render={({ field }) => (
                        <Select 
                          {...field} 
                          label="Category *"
                          startAdornment={
                            <InputAdornment position="start">
                              <CategoryIcon color="primary" />
                            </InputAdornment>
                          }
                          sx={{
                            borderRadius: 2,
                            '& fieldset': {
                              borderWidth: 2
                            }
                          }}
                        >
                          <MenuItem value="">Select Category</MenuItem>
                          {categories.map(cat => (
                            <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                          ))}
                        </Select>
                      )}
                    />
                    {errors.category_id && (
                      <FormHelperText>{errors.category_id.message}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth error={!!errors.format}>
                    <InputLabel>Format *</InputLabel>
                    <Controller
                      name="format"
                      control={control}
                      render={({ field }) => (
                        <Select 
                          {...field} 
                          label="Format *"
                          sx={{
                            borderRadius: 2,
                            '& fieldset': {
                              borderWidth: 2
                            }
                          }}
                        >
                          <MenuItem value="">Select Format</MenuItem>
                          {formats.map(fmt => (
                            <MenuItem key={fmt.id} value={fmt.id}>{fmt.name}</MenuItem>
                          ))}
                        </Select>
                      )}
                    />
                    {errors.format && (
                      <FormHelperText>{errors.format.message}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth error={!!errors.school_year_id}>
                    <InputLabel>School year *</InputLabel>
                    <Controller
                      name="school_year_id"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          value={field.value ?? ''}
                          label="School year *"
                          sx={{
                            borderRadius: 2,
                            '& fieldset': { borderWidth: 2 }
                          }}
                        >
                          <MenuItem value="">Select school year</MenuItem>
                          {schoolYears.map((y) => (
                            <MenuItem key={y.id} value={y.id}>{y.name}</MenuItem>
                          ))}
                        </Select>
                      )}
                    />
                    {errors.school_year_id && (
                      <FormHelperText>{errors.school_year_id.message}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth error={!!errors.subject_ids}>
                    <InputLabel>Subjects *</InputLabel>
                    <Controller
                      name="subject_ids"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          multiple
                          value={Array.isArray(field.value) ? field.value : []}
                          onChange={(e) => {
                            const v = e.target.value;
                            field.onChange(typeof v === 'string' ? v.split(',').map(Number) : v);
                          }}
                          input={<OutlinedInput label="Subjects *" />}
                          renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {(selected || []).map((id) => {
                                const s = subjectsList.find((x) => Number(x.id) === Number(id));
                                return <Chip key={id} label={s?.name ?? id} size="small" />;
                              })}
                            </Box>
                          )}
                          label="Subjects *"
                          sx={{
                            borderRadius: 2,
                            '& fieldset': { borderWidth: 2 }
                          }}
                        >
                          {subjectsList.map((s) => (
                            <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                          ))}
                        </Select>
                      )}
                    />
                    {errors.subject_ids && (
                      <FormHelperText>{errors.subject_ids.message}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <FormControl fullWidth error={!!errors.target_school_ids}>
                    <InputLabel>Target Schools</InputLabel>
                    <Controller
                      name="target_school_ids"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          multiple
                          label="Target Schools"
                          value={field.value || []}
                          renderValue={(selected) =>
                            (selected || [])
                              .map((id) => schools.find((s) => String(s.id) === String(id))?.name)
                              .filter(Boolean)
                              .join(', ')
                          }
                        >
                          {(Array.isArray(schools) ? schools : []).map((s) => {
                            const sid = String(s.id);
                            const selected = (field.value || []).map(String).includes(sid);
                            return (
                              <MenuItem key={s.id} value={sid}>
                                <Checkbox checked={selected} size="small" />
                                <ListItemText primary={s.name} />
                              </MenuItem>
                            );
                          })}
                        </Select>
                      )}
                    />
                    {errors.target_school_ids && (
                      <FormHelperText>{errors.target_school_ids.message}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                {/* Price & Currency */}
                <Grid item xs={12} md={6}>
                  <Controller
                    name="price"
                    control={control}
                    render={({ field }) => {
                      const cur = watchedCurrency || "£";
                      const sym = cur === "€" ? "€" : cur === "$" ? "$" : "£";
                      return (
                      <TextField
                        {...field}
                        label="Price"
                        type="number"
                        fullWidth
                        required
                        error={!!errors.price}
                        helperText={errors.price?.message}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Typography component="span" variant="body2" sx={{ fontWeight: 600, color: "text.secondary" }}>
                                {sym}
                              </Typography>
                            </InputAdornment>
                          )
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            '& fieldset': {
                              borderWidth: 2
                            }
                          }
                        }}
                      />
                      );
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth error={!!errors.currency}>
                    <InputLabel>Currency</InputLabel>
                    <Controller
                      name="currency"
                      control={control}
                      render={({ field }) => (
                        <Select 
                          {...field} 
                          label="Currency"
                          sx={{
                            borderRadius: 2,
                            '& fieldset': {
                              borderWidth: 2
                            }
                          }}
                        >
                          <MenuItem value="£">£ Pound</MenuItem>
                          <MenuItem value="€">€ Euro</MenuItem>
                          <MenuItem value="$">$ Dollar</MenuItem>
                        </Select>
                      )}
                    />
                    {errors.currency && (
                      <FormHelperText>{errors.currency.message}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <Controller
                    name="is_free_paper"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={!!field.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                          />
                        }
                        label="Free past paper (show as FREE in Past Papers section)"
                      />
                    )}
                  />
                </Grid>

                {/* Paper Image */}
                <Grid item xs={12}>
                  <Card sx={{ 
                    p: 3, 
                    background: alpha('#667eea', 0.05),
                    border: '2px dashed',
                    borderColor: alpha('#667eea', 0.3),
                    borderRadius: 3
                  }}>
                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 700, color: '#667eea', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CloudUploadIcon />
                      Paper Thumbnail Image
                    </Typography>
                    <Controller
                      name="paper_image"
                      control={control}
                      render={({ field: { onChange, value, ...field } }) => (
                        <>
                          <Button
                            variant="contained"
                            component="label"
                            startIcon={<CloudUploadIcon />}
                            sx={{ 
                              mb: 2,
                              borderRadius: 2,
                              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                              textTransform: 'none',
                              fontWeight: 600,
                              px: 4,
                              '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: '0 8px 24px rgba(102, 126, 234, 0.4)'
                              }
                            }}
                          >
                            Upload Image
                            <input
                              type="file"
                              hidden
                              accept="image/*"
                              onChange={(e) => {
                                const file = e.target.files[0];
                                onChange(file);
                                if (file) toast.success(`Image selected: ${file.name}`);
                              }}
                              {...field}
                            />
                          </Button>
                          {value && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                              <Alert severity="success" icon={<CheckIcon />} sx={{ flex: 1 }}>
                                {value.name}
                              </Alert>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <Tooltip title="Preview Image" arrow>
                                  <IconButton
                                    onClick={() => handleImagePreview(value)}
                                    sx={{
                                      background: alpha('#2196f3', 0.1),
                                      '&:hover': {
                                        background: alpha('#2196f3', 0.2)
                                      }
                                    }}
                                  >
                                    <PreviewIcon sx={{ color: '#2196f3' }} />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Remove Image" arrow>
                                  <IconButton
                                    onClick={() => {
                                      onChange(null);
                                      toast.info('Image removed');
                                    }}
                                    sx={{
                                      background: alpha('#f44336', 0.1),
                                      '&:hover': {
                                        background: alpha('#f44336', 0.2)
                                      }
                                    }}
                                  >
                                    <DeleteIcon sx={{ color: '#f44336' }} />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </Box>
                          )}
                          {existingImageUrl && !value && (
                            <Box sx={{ mt: 2, width: '100%', overflow: 'hidden' }}>
                              <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'flex-start', 
                                gap: 2,
                                p: 2,
                                background: alpha('#667eea', 0.03),
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: alpha('#667eea', 0.2),
                                flexWrap: 'wrap',
                                width: '100%',
                                maxWidth: '100%',
                                boxSizing: 'border-box'
                              }}>
                                <Box sx={{ 
                                  position: 'relative',
                                  flexShrink: 0
                                }}>
                                  <img 
                                    src={existingImageUrl} 
                                    alt="Current" 
                                    style={{ 
                                      width: 120,
                                      height: 120,
                                      objectFit: 'cover',
                                      borderRadius: 12,
                                      boxShadow: '0 8px 24px rgba(0,0,0,0.2)'
                                    }} 
                                  />
                                </Box>
                                <Box sx={{ 
                                  display: 'flex', 
                                  flexDirection: 'column', 
                                  gap: 1.5,
                                  flex: '1 1 auto',
                                  minWidth: 0,
                                  maxWidth: 'calc(100% - 140px)',
                                  overflow: 'hidden'
                                }}>
                                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                    Current Thumbnail
                                  </Typography>
                                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    <Tooltip title="Preview Image" arrow>
                                      <IconButton
                                        onClick={() => handleImagePreview(existingImageUrl)}
                                        size="small"
                                        sx={{
                                          background: alpha('#2196f3', 0.1),
                                          '&:hover': {
                                            background: alpha('#2196f3', 0.2),
                                            transform: 'scale(1.1)'
                                          }
                                        }}
                                      >
                                        <PreviewIcon sx={{ color: '#2196f3', fontSize: 20 }} />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete Image" arrow>
                                      <IconButton
                                        onClick={() => {
                                          setExistingImageUrl(null);
                                          setRemoveImage(true);
                                          toast.info('Image will be removed on update');
                                        }}
                                        size="small"
                                        sx={{
                                          background: alpha('#f44336', 0.1),
                                          '&:hover': {
                                            background: alpha('#f44336', 0.2),
                                            transform: 'scale(1.1)'
                                          }
                                        }}
                                      >
                                        <DeleteIcon sx={{ color: '#f44336', fontSize: 20 }} />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                </Box>
                              </Box>
                            </Box>
                          )}
                          {errors.paper_image && (
                            <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
                              {errors.paper_image.message}
                            </Typography>
                          )}
                        </>
                      )}
                    />
                  </Card>
                </Grid>

                {/* Questions - Add one by one */}
                <Grid item xs={12}>
                  <Card sx={{ 
                    p: 4, 
                    background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
                    border: '2px solid',
                    borderColor: alpha('#667eea', 0.3),
                    borderRadius: 4,
                    boxShadow: '0 8px 32px rgba(102, 126, 234, 0.1)'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', width: 56, height: 56 }}>
                          <AddIcon sx={{ fontSize: 28 }} />
                        </Avatar>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: '#667eea' }}>Questions</Typography>
                          <Typography variant="body2" color="text.secondary">
                            Question bank (same as Mock Exams) plus optional manual MCQ blocks below.
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    {editingPaper && (
                      <MuiPaper
                        sx={{
                          p: 3,
                          borderRadius: 3,
                          mb: 3,
                          border: '1px solid',
                          borderColor: 'rgba(63,43,150,0.2)',
                          background: 'linear-gradient(135deg, rgba(63,43,150,0.04) 0%, rgba(167,29,49,0.04) 100%)',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <QuizIcon sx={{ color: '#3f2b96' }} />
                            <Typography variant="h6" sx={{ fontWeight: 600, color: '#263238' }}>
                              Questions from Question Bank
                            </Typography>
                            {questionBankExtractPreview.length > 0 && (
                              <Chip label={`${questionBankExtractPreview.length} main Q`} size="small" color="primary" />
                            )}
                          </Box>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => openAssignQuestionBank(editingPaper)}
                            disabled={editingPaper.status !== 2}
                            sx={{
                              background: 'linear-gradient(to right, #3f2b96, #a71d31)',
                              textTransform: 'none',
                              fontWeight: 600,
                            }}
                          >
                            Select Questions
                          </Button>
                        </Box>
                        {questionBankExtractPreview.length > 0 && (() => {
                          const qMarks = (q) => {
                            if (q.type === 'grouped' && Array.isArray(q.sub_questions) && q.sub_questions.length > 0) {
                              const subSum = q.sub_questions.reduce((s, sq) => s + getQuestionEffectiveMarks(sq), 0);
                              return subSum > 0 ? subSum : getQuestionEffectiveMarks(q);
                            }
                            return getQuestionEffectiveMarks(q) || 1;
                          };
                          const totalExtractMarks = questionBankExtractPreview.reduce((sum, q) => sum + qMarks(q), 0);
                          const over = totalExtractMarks > 100;
                          const exact = totalExtractMarks === 100;
                          const remaining = 100 - totalExtractMarks;
                          return (
                            <Box sx={{ mb: 2, p: 1.5, borderRadius: 2, border: '1px solid', borderColor: over ? 'error.main' : exact ? 'success.main' : 'divider', bgcolor: over ? 'rgba(244,67,54,0.04)' : exact ? 'rgba(76,175,80,0.04)' : 'background.paper' }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75, flexWrap: 'wrap', gap: 1 }}>
                                <Typography variant="caption" fontWeight={600} color={over ? 'error.main' : 'text.secondary'}>
                                  Total marks — {questionBankExtractPreview.length} question{questionBankExtractPreview.length !== 1 ? 's' : ''}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="caption" fontWeight={700} color={over ? 'error.main' : exact ? 'success.main' : 'text.primary'}>
                                    {totalExtractMarks} / 100
                                    {over ? ' — exceeds 100!' : exact ? ' ✓' : ` (${remaining} more needed)`}
                                  </Typography>
                                  {!exact && (
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      disabled={scalingMarks || totalExtractMarks === 0}
                                      onClick={handleScaleTo100}
                                      sx={{ textTransform: 'none', fontSize: '0.72rem', py: 0.25, px: 1, minWidth: 'auto', borderColor: over ? 'error.main' : 'primary.main', color: over ? 'error.main' : 'primary.main' }}
                                    >
                                      {scalingMarks ? <CircularProgress size={12} /> : 'Scale to 100'}
                                    </Button>
                                  )}
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    disabled={scalingMarks}
                                    onClick={handleRenumber}
                                    sx={{ textTransform: 'none', fontSize: '0.72rem', py: 0.25, px: 1, minWidth: 'auto' }}
                                  >
                                    Renumber 1…N
                                  </Button>
                                </Box>
                              </Box>
                              <LinearProgress
                                variant="determinate"
                                value={Math.min((totalExtractMarks / 100) * 100, 100)}
                                color={over ? 'error' : totalExtractMarks >= 90 ? 'warning' : 'primary'}
                                sx={{ borderRadius: 1, height: 8 }}
                              />
                            </Box>
                          );
                        })()}
                        {questionBankExtractPreview.length === 0 ? (
                          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                            No questions selected from bank. Click &ldquo;Select Questions&rdquo; to add.
                          </Typography>
                        ) : (
                          <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={handleExtractDragEnd}>
                            <SortableContext items={questionBankExtractPreview.map((q) => String(q.id))} strategy={verticalListSortingStrategy}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {(() => {
                              let lastSectionTitle = null;
                              return questionBankExtractPreview.map((q, idx) => {
                                const isNewSection = (q.section_title || null) !== lastSectionTitle;
                                lastSectionTitle = q.section_title || null;
                                const sectionHeader = isNewSection && q.section_title ? (
                                  inlineSectionEdit?.sectionTitle === q.section_title ? (
                                    <Box key={`sec-edit-${q.section_title}`} sx={{ px: 1.5, py: 1, bgcolor: alpha('#1565c0', 0.06), borderRadius: 1, borderLeft: '3px solid #1565c0', display: 'flex', flexDirection: 'column', gap: 1, mb: 0.5 }}>
                                      <TextField
                                        size="small"
                                        autoFocus
                                        label="Section title"
                                        value={inlineSectionEdit.newTitle}
                                        onChange={(e) => setInlineSectionEdit((s) => ({ ...s, newTitle: e.target.value }))}
                                        onKeyDown={(e) => { if (e.key === 'Enter') commitInlineSectionEdit(); if (e.key === 'Escape') setInlineSectionEdit(null); }}
                                        sx={{ bgcolor: 'white' }}
                                      />
                                      <TextField
                                        size="small"
                                        label="Section instructions"
                                        multiline
                                        rows={2}
                                        value={inlineSectionEdit.newInstructions}
                                        onChange={(e) => setInlineSectionEdit((s) => ({ ...s, newInstructions: e.target.value }))}
                                        onKeyDown={(e) => { if (e.key === 'Escape') setInlineSectionEdit(null); }}
                                        sx={{ bgcolor: 'white' }}
                                      />
                                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                                        <Button size="small" disabled={inlineSectionSaving} onClick={() => setInlineSectionEdit(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
                                        <Button size="small" variant="outlined" color="error" disabled={inlineSectionSaving} onClick={removeInlineSectionBreak} sx={{ textTransform: 'none' }}>
                                          {inlineSectionSaving ? <CircularProgress size={14} /> : 'Remove break'}
                                        </Button>
                                        <Button size="small" variant="contained" disabled={inlineSectionSaving} onClick={commitInlineSectionEdit} sx={{ textTransform: 'none' }}>
                                          {inlineSectionSaving ? <CircularProgress size={14} /> : 'Save'}
                                        </Button>
                                      </Box>
                                    </Box>
                                  ) : (
                                    <Box
                                      key={`sec-${q.section_title}-${idx}`}
                                      onClick={() => setInlineSectionEdit({ sectionTitle: q.section_title, newTitle: q.section_title, newInstructions: q.section_instructions || '' })}
                                      sx={{ px: 1.5, py: 0.75, mb: 0.5, bgcolor: alpha('#1565c0', 0.06), borderRadius: 1, borderLeft: '3px solid #1565c0', cursor: 'pointer', '&:hover': { bgcolor: alpha('#1565c0', 0.12) } }}
                                    >
                                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#1565c0', textTransform: 'uppercase' }}>
                                        {q.section_title}
                                        <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary', fontWeight: 400, textTransform: 'none' }}>(click to edit)</Typography>
                                      </Typography>
                                      {q.section_instructions && (
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, fontStyle: 'italic' }}>
                                          {q.section_instructions}
                                        </Typography>
                                      )}
                                    </Box>
                                  )
                                ) : null;
                                return (
                                  <SortablePaperQuestionRow key={q.id} id={String(q.id)}>
                                    {({ dragHandleProps }) => (
                                  <React.Fragment>
                                    {qSectionEdit?.qId === q.id && (
                                      <Box sx={{ px: 1.5, py: 1.5, mb: 0.5, bgcolor: alpha('#1565c0', 0.06), borderRadius: 1, borderLeft: '3px solid #1565c0', display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#1565c0', textTransform: 'uppercase' }}>
                                          Section break before Q{idx + 1}
                                        </Typography>
                                        <TextField
                                          size="small"
                                          autoFocus
                                          label="Section title"
                                          value={qSectionEdit.title}
                                          onChange={(e) => setQSectionEdit((s) => ({ ...s, title: e.target.value }))}
                                          onKeyDown={(e) => { if (e.key === 'Escape') setQSectionEdit(null); }}
                                          placeholder="e.g. Section A"
                                          sx={{ bgcolor: 'white' }}
                                        />
                                        <TextField
                                          size="small"
                                          label="Section instructions"
                                          multiline
                                          rows={2}
                                          value={qSectionEdit.instructions}
                                          onChange={(e) => setQSectionEdit((s) => ({ ...s, instructions: e.target.value }))}
                                          onKeyDown={(e) => { if (e.key === 'Escape') setQSectionEdit(null); }}
                                          placeholder="Instructions shown at the start of this section (optional)"
                                          sx={{ bgcolor: 'white' }}
                                        />
                                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                                          <Button size="small" disabled={qSectionSaving} onClick={() => setQSectionEdit(null)} sx={{ textTransform: 'none' }}>
                                            Cancel
                                          </Button>
                                          {q.section_title && (
                                            <Button size="small" variant="outlined" color="error" disabled={qSectionSaving} onClick={() => removeQSectionBreak(q.id)} sx={{ textTransform: 'none' }}>
                                              {qSectionSaving ? <CircularProgress size={14} /> : 'Remove break'}
                                            </Button>
                                          )}
                                          <Button size="small" variant="contained" disabled={qSectionSaving} onClick={saveQSectionEdit} sx={{ textTransform: 'none' }}>
                                            {qSectionSaving ? <CircularProgress size={14} /> : 'Save'}
                                          </Button>
                                        </Box>
                                      </Box>
                                    )}
                                    {sectionHeader}
                                    <MuiPaper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2, overflow: 'hidden', maxWidth: '100%' }}>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: { xs: 1, sm: 2 }, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                                  <TextField
                                    size="small"
                                    label="Paper #"
                                    value={editingPaperNumbers[q.id] ?? (q.paper_number ?? '')}
                                    onChange={(e) => setEditingPaperNumbers((prev) => ({ ...prev, [q.id]: e.target.value }))}
                                    onBlur={() => commitPaperNumber(q, editingPaperNumbers[q.id] ?? (q.paper_number ?? ''))}
                                    onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                                    placeholder="e.g. 31"
                                    sx={{
                                      width: 72,
                                      flexShrink: 0,
                                      '& .MuiInputBase-input': { fontFamily: 'monospace', fontWeight: 600, fontSize: '0.75rem', py: 0.5, px: 1 },
                                      '& .MuiInputLabel-root': { fontSize: '0.7rem' },
                                    }}
                                  />
                                  {q.image_url && (
                                    <Box sx={{ flexShrink: 0 }}>
                                      <img
                                        src={q.image_url}
                                        alt=""
                                        style={{ width: 80, height: 60, objectFit: 'cover', borderRadius: 6, border: '1px solid #e0e0e0' }}
                                      />
                                    </Box>
                                  )}
                                  <Box sx={{ flex: 1, minWidth: { xs: '100%', sm: 0 }, overflow: 'hidden' }}>
                                    <Box sx={{ mb: 0.5, maxHeight: 160, overflowX: 'auto', overflowY: 'auto', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                                      <HtmlWithLatex html={q.question_text || ''} />
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5, alignItems: 'center' }}>
                                      <Chip label={q.type?.replace(/-/g, ' ') || 'MCQ'} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                                      <Chip label={q.difficulty || 'Medium'} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                                      {q.type === 'grouped' && Array.isArray(q.sub_questions) && q.sub_questions.length > 0 ? (
                                        <>
                                          {(() => {
                                            const subs = q.sub_questions;
                                            const count = subs.length;
                                            const parentAd = q.answer_data || {};
                                            if (parentAd.marks_mode === 'fixed') {
                                              const fixedTotal = parentAd.marks_total ?? q.default_marks ?? 1;
                                              return (
                                                <Chip
                                                  label={`${fixedTotal}m / ${count} parts`}
                                                  size="small" color="primary" variant="outlined" sx={{ fontSize: '0.7rem', fontWeight: 700 }}
                                                />
                                              );
                                            }
                                            const marks = subs.map((sq) => getQuestionEffectiveMarks(sq));
                                            const total = marks.reduce((s, m) => s + m, 0);
                                            const allEqual = marks.every((m) => m === marks[0]);
                                            const parentM = parseInt(q.default_marks, 10) || 0;
                                            if (parentM > 0 && allEqual && marks[0] > 0 && total > parentM && parentM !== marks[0]) {
                                              return (
                                                <Chip
                                                  label={`${parentM}m / ${count} parts`}
                                                  size="small" color="warning" variant="outlined" sx={{ fontSize: '0.7rem', fontWeight: 700 }}
                                                  title={`Auto-detected: ${parentM} marks ÷ ${count} parts (${parentM}/${count} each)`}
                                                />
                                              );
                                            }
                                            return (
                                              <Chip
                                                label={allEqual && marks[0] > 0 ? `${marks[0]}m × ${count}` : `${count} parts · ${total || q.default_marks || 0}m`}
                                                size="small" color="primary" variant="outlined" sx={{ fontSize: '0.7rem', fontWeight: 700 }}
                                              />
                                            );
                                          })()}
                                        </>
                                      ) : (
                                        <Chip
                                          label={getQuestionMarksChipLabel(q)}
                                          size="small" color="primary" variant="outlined" sx={{ fontSize: '0.7rem', fontWeight: 700 }}
                                        />
                                      )}
                                    </Box>
                                  </Box>
                                  <Box sx={{ display: 'flex', flexDirection: { xs: 'row', sm: 'column' }, flexWrap: { xs: 'wrap', sm: 'nowrap' }, gap: 0.5, flexShrink: 0 }}>
                                    <Tooltip title="Drag to reorder" arrow>
                                      <IconButton size="small" sx={{ color: '#bdbdbd', cursor: 'grab' }} {...dragHandleProps}>
                                        <DragIndicatorIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Move up" arrow>
                                      <span>
                                        <IconButton
                                          size="small"
                                          onClick={() => moveExtractQuestion(idx, 'up')}
                                          disabled={idx === 0 || paperExtractRemovingId === q.id}
                                          sx={{ color: '#5c6bc0' }}
                                        >
                                          <ArrowUpIcon fontSize="small" />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                    <Tooltip title="Move down" arrow>
                                      <span>
                                        <IconButton
                                          size="small"
                                          onClick={() => moveExtractQuestion(idx, 'down')}
                                          disabled={idx === questionBankExtractPreview.length - 1 || paperExtractRemovingId === q.id}
                                          sx={{ color: '#5c6bc0' }}
                                        >
                                          <ArrowDownIcon fontSize="small" />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                    <Tooltip title="Add new question before" arrow>
                                      <span>
                                        <IconButton
                                          size="small"
                                          onClick={() => setAddQDialog({ open: true, mode: 'before', targetIdx: idx, parentQ: q })}
                                          disabled={paperExtractRemovingId === q.id}
                                          sx={{ bgcolor: alpha('#0288d1', 0.1), '&:hover': { bgcolor: alpha('#0288d1', 0.2) } }}
                                        >
                                          <AddIcon sx={{ fontSize: 14, color: '#0288d1' }} />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                    <Tooltip title="Add new question after" arrow>
                                      <span>
                                        <IconButton
                                          size="small"
                                          onClick={() => setAddQDialog({ open: true, mode: 'after', targetIdx: idx, parentQ: q })}
                                          disabled={paperExtractRemovingId === q.id}
                                          sx={{ bgcolor: alpha('#0288d1', 0.1), '&:hover': { bgcolor: alpha('#0288d1', 0.2) } }}
                                        >
                                          <AddIcon sx={{ fontSize: 14, color: '#0288d1' }} />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                    <Tooltip title={q.section_title ? `Edit section: "${q.section_title}"` : 'Add section break before this question'} arrow>
                                      <IconButton
                                        size="small"
                                        onClick={() => setQSectionEdit({ qId: q.id, title: q.section_title || '', instructions: q.section_instructions || '' })}
                                        disabled={paperExtractRemovingId === q.id}
                                        sx={{ color: q.section_title ? '#1565c0' : alpha('#1565c0', 0.5) }}
                                      >
                                        <SectionIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Edit question" arrow>
                                      <IconButton
                                        size="small"
                                        color="primary"
                                        onClick={() => openPaperBankQuestionEdit(q)}
                                        disabled={paperExtractRemovingId === q.id}
                                      >
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Remove from paper" arrow>
                                      <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => setRemoveQDialog({ open: true, question: q })}
                                        disabled={paperExtractRemovingId === q.id}
                                      >
                                        {paperExtractRemovingId === q.id ? <CircularProgress size={18} /> : <DeleteIcon fontSize="small" />}
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                </Box>
                                {(Array.isArray(q.sub_questions) && q.sub_questions.length > 0) || canUpdatePapers ? (
                                  <Box sx={{ mt: 1.5, pl: { xs: 1, sm: 4 }, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                    {Array.isArray(q.sub_questions) && q.sub_questions.length > 0 && (
                                      <DndContext
                                        sensors={dndSensors}
                                        collisionDetection={closestCenter}
                                        onDragEnd={({ active, over }) => {
                                          if (!active?.id || !over?.id || String(active.id) === String(over.id)) return;
                                          const ids = q.sub_questions.map((s) => String(s.id));
                                          const oldIdx = ids.indexOf(String(active.id));
                                          const newIdx = ids.indexOf(String(over.id));
                                          if (oldIdx < 0 || newIdx < 0) return;
                                          syncSubQuestionOrder(q, arrayMove(q.sub_questions, oldIdx, newIdx));
                                        }}
                                      >
                                        <SortableContext
                                          items={q.sub_questions.map((s) => String(s.id))}
                                          strategy={verticalListSortingStrategy}
                                        >
                                          {q.sub_questions.map((sub, subIdx) => {
                                            const subAutoLabel = `${String.fromCharCode(97 + subIdx)})`;
                                            return (
                                              <SortableSubQuestionRow key={sub.id} id={String(sub.id)}>
                                                {({ dragHandleProps: subDragHandleProps }) => (
                                                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', p: 1, borderRadius: 1, bgcolor: alpha('#667eea', 0.04), border: '1px solid', borderColor: alpha('#667eea', 0.12), flexWrap: 'wrap' }}>
                                                    <Tooltip title="Drag to reorder" arrow>
                                                      <IconButton size="small" sx={{ color: '#bdbdbd', cursor: 'grab', p: 0.25 }} {...subDragHandleProps}>
                                                        <DragIndicatorIcon sx={{ fontSize: 14 }} />
                                                      </IconButton>
                                                    </Tooltip>
                                                    <TextField
                                                      size="small"
                                                      label="Paper #"
                                                      value={editingPaperNumbers[sub.id] ?? (sub.paper_number ?? '')}
                                                      onChange={(e) => setEditingPaperNumbers((prev) => ({ ...prev, [sub.id]: e.target.value }))}
                                                      onBlur={() => commitPaperNumber(sub, editingPaperNumbers[sub.id] ?? (sub.paper_number ?? ''))}
                                                      onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                                                      placeholder={subAutoLabel}
                                                      sx={{
                                                        width: 64,
                                                        flexShrink: 0,
                                                        '& .MuiInputBase-input': { fontFamily: 'monospace', fontWeight: 600, fontSize: '0.7rem', py: 0.4, px: 0.75 },
                                                        '& .MuiInputLabel-root': { fontSize: '0.65rem' },
                                                      }}
                                                    />
                                                    <Box sx={{ flex: 1, minWidth: { xs: '60%', sm: 0 }, overflow: 'hidden', wordBreak: 'break-word' }}>
                                                      <HtmlWithLatex html={sub.question_text || ''} style={{ fontSize: '0.82rem' }} />
                                                    </Box>
                                                    <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                                      <Chip label={sub.type?.replace(/-/g, ' ') || 'Q'} size="small" variant="outlined" sx={{ fontSize: '0.65rem' }} />
                                                      {(() => {
                                                        const parentAd = q.answer_data || {};
                                                        const subCount = (q.sub_questions || []).length;
                                                        if (parentAd.marks_mode === 'fixed') {
                                                          const total = parentAd.marks_total ?? q.default_marks ?? 1;
                                                          return <Chip label={`${total}/${subCount}`} size="small" color="primary" variant="outlined" sx={{ fontSize: '0.65rem', fontWeight: 700 }} title={`${total} marks total ÷ ${subCount} parts`} />;
                                                        }
                                                        const subMarks = (q.sub_questions || []).map((s) => parseInt(s.default_marks, 10) || 0);
                                                        const subSum = subMarks.reduce((a, b) => a + b, 0);
                                                        const parentMarks = parseInt(q.default_marks, 10) || 0;
                                                        const allEqual = subMarks.length > 1 && subMarks.every((m) => m === subMarks[0]);
                                                        if (parentMarks > 0 && subSum > parentMarks && allEqual) {
                                                          return <Chip label={`${parentMarks}/${subCount}`} size="small" color="warning" variant="outlined" sx={{ fontSize: '0.65rem', fontWeight: 700 }} title={`Auto-detected: ${parentMarks} marks total across ${subCount} parts`} />;
                                                        }
                                                        return <Chip label={getQuestionMarksChipLabel(sub)} size="small" color={!getQuestionEffectiveMarks(sub) ? 'error' : 'primary'} variant={!getQuestionEffectiveMarks(sub) ? 'filled' : 'outlined'} sx={{ fontSize: '0.65rem', fontWeight: 700 }} />;
                                                      })()}
                                                      <Tooltip title="Remove sub-question" arrow>
                                                        <IconButton
                                                          size="small"
                                                          color="error"
                                                          onClick={() => removeSubQuestion(q, subIdx)}
                                                          disabled={removingSubQId === sub.id}
                                                        >
                                                          {removingSubQId === sub.id ? <CircularProgress size={14} /> : <DeleteIcon sx={{ fontSize: 14 }} />}
                                                        </IconButton>
                                                      </Tooltip>
                                                    </Box>
                                                  </Box>
                                                )}
                                              </SortableSubQuestionRow>
                                            );
                                          })}
                                        </SortableContext>
                                      </DndContext>
                                    )}
                                    {canUpdatePapers && (
                                      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          startIcon={<AddIcon />}
                                          onClick={() => { setAddSubQParent(q); setBankPickerOpen(true); }}
                                          sx={{ textTransform: 'none', fontSize: '0.72rem', fontWeight: 600 }}
                                        >
                                          Add from bank
                                        </Button>
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          color="secondary"
                                          startIcon={<AddIcon />}
                                          onClick={() => setAddQDialog({ open: true, mode: 'sub-question', targetIdx: idx, parentQ: q })}
                                          sx={{ textTransform: 'none', fontSize: '0.72rem', fontWeight: 600 }}
                                        >
                                          Create new
                                        </Button>
                                      </Box>
                                    )}
                                  </Box>
                                ) : null}
                              </MuiPaper>
                                  {gapsByIndex[idx]?.map((n) => (
                                    <Box key={`gap-${n}`} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 0.75, my: 0.5, bgcolor: alpha('#ff9800', 0.07), border: '1px dashed', borderColor: alpha('#ff9800', 0.5), borderRadius: 1 }}>
                                      <Typography variant="caption" sx={{ color: '#e65100', fontWeight: 700, flexGrow: 1 }}>
                                        Q{n} missing
                                      </Typography>
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        color="warning"
                                        sx={{ textTransform: 'none', fontSize: '0.7rem', py: 0.25 }}
                                        onClick={() => { setMissingSlotNumber(n); setBankPickerOpen(true); }}
                                      >
                                        + Add question here
                                      </Button>
                                    </Box>
                                  ))}
                                  </React.Fragment>
                                    )}
                                  </SortablePaperQuestionRow>
                                );
                              });
                            })()}
                          </Box>
                            </SortableContext>
                          </DndContext>
                        )}
                      </MuiPaper>
                    )}

                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#667eea' }}>
                        Manual questions (optional)
                      </Typography>
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={addQuestion}
                        sx={{
                          borderRadius: 2,
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          fontWeight: 700,
                          textTransform: 'none',
                        }}
                      >
                        Add Question
                      </Button>
                    </Box>

                    {questions.map((q, qIndex) => (
                      <Card key={qIndex} variant="outlined" sx={{ p: 2, mb: 2, borderColor: alpha('#667eea', 0.3), borderRadius: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                          <Typography variant="subtitle1" fontWeight={700} color="#667eea">Question {qIndex + 1}</Typography>
                          <IconButton size="small" onClick={() => removeQuestion(qIndex)} sx={{ color: '#f44336' }} title="Remove question">
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>
                            Question text
                          </Typography>
                          <Box
                            sx={{
                              '& .ql-toolbar': { borderTopLeftRadius: 8, borderTopRightRadius: 8, borderColor: 'divider' },
                              '& .ql-container': {
                                minHeight: 120,
                                borderBottomLeftRadius: 8,
                                borderBottomRightRadius: 8,
                                borderColor: 'divider',
                                fontSize: '1rem',
                              },
                              '& .quill': { background: '#fff', borderRadius: 1 },
                            }}
                          >
                            <ReactQuill
                              theme="snow"
                              value={q.question_text || ''}
                              onChange={(html) => updateQuestion(qIndex, 'question_text', html)}
                              modules={QUILL_MODULES}
                              formats={QUILL_FORMATS}
                              placeholder="Enter question text..."
                            />
                          </Box>
                        </Box>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              type="number"
                              label="Marks"
                              value={q.marks}
                              onChange={(e) => updateQuestion(qIndex, 'marks', parseInt(e.target.value, 10) || 1)}
                              inputProps={{ min: 1 }}
                              size="small"
                              fullWidth
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              type="number"
                              label="Duration (seconds)"
                              value={q.duration_in_sec}
                              onChange={(e) => updateQuestion(qIndex, 'duration_in_sec', parseInt(e.target.value, 10) || 60)}
                              inputProps={{ min: 1 }}
                              size="small"
                              fullWidth
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={!!q.is_free}
                                  onChange={(e) => updateQuestion(qIndex, 'is_free', e.target.checked)}
                                />
                              }
                              label="Free sample (show as teaser on the public Papers page)"
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Autocomplete
                              multiple
                              options={topicOptions}
                              getOptionLabel={(o) => (o && (o.label || o.name)) || ''}
                              value={topicOptions.filter((o) => (q.topic_ids || []).includes(o.id))}
                              onChange={(_, newVal) => {
                                updateQuestion(qIndex, 'topic_ids', (newVal || []).map((x) => x.id));
                              }}
                              disabled={!topicOptions.length}
                              isOptionEqualToValue={(a, b) => a.id === b.id}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Topics"
                                  placeholder={topicOptions.length ? 'Select one or more topics' : 'Select subjects & school year above first'}
                                  size="small"
                                />
                              )}
                              renderTags={(value, getTagProps) =>
                                value.map((option, index) => (
                                  <Chip label={option.label || option.name} size="small" {...getTagProps({ index })} key={option.id} />
                                ))
                              }
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                              Topics are loaded for the selected subject(s) and school year. Admins can scope topics by school year in the Topics master data.
                            </Typography>
                          </Grid>
                        </Grid>
                        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>Options</Typography>
                        {(q.options || []).map((opt, optIndex) => (
                          <Box key={optIndex} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                            <TextField
                              size="small"
                              fullWidth
                              placeholder={`Option ${optIndex + 1}`}
                              value={opt}
                              onChange={(e) => updateOption(qIndex, optIndex, e.target.value)}
                            />
                            <IconButton
                              size="small"
                              onClick={() => removeOption(qIndex, optIndex)}
                              disabled={(q.options || []).length <= 2}
                              sx={{ color: '#f44336' }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        ))}
                        <Button size="small" startIcon={<AddIcon />} onClick={() => addOption(qIndex)} sx={{ mt: 1 }}>
                          Add option
                        </Button>
                        <FormControl fullWidth size="small" sx={{ mt: 2 }}>
                          <InputLabel>Correct answer</InputLabel>
                          <Select
                            value={q.correct_answer || ''}
                            onChange={(e) => updateQuestion(qIndex, 'correct_answer', e.target.value)}
                            label="Correct answer"
                          >
                            {(q.options || []).filter(o => (o || '').toString().trim()).map((opt, idx) => (
                              <MenuItem key={idx} value={opt.trim()}>{opt.trim() || `Option ${idx + 1}`}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Card>
                    ))}
                  </Card>
                </Grid>

                {/* Submit Buttons */}
                <Grid item xs={12}>
                  <Box sx={{ 
                    display: 'flex', 
                    gap: 3,
                    pt: 3,
                    borderTop: '2px solid',
                    borderColor: 'divider'
                  }}>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={
                        isSubmitting
                        || (editingPaper ? !canUpdatePapers : !canCreatePapers)
                      }
                      startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                      sx={{
                        flex: 1,
                        py: 2,
                        borderRadius: 3,
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        fontSize: '18px',
                        fontWeight: 700,
                        textTransform: 'none',
                        boxShadow: '0 8px 24px rgba(102, 126, 234, 0.4)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-3px)',
                          boxShadow: '0 12px 32px rgba(102, 126, 234, 0.5)'
                        },
                        '&:disabled': {
                          background: alpha('#667eea', 0.5)
                        }
                      }}
                    >
                      {isSubmitting ? 'Saving...' : (editingPaper ? 'Update Paper' : 'Create Paper')}
                    </Button>
                    {editingPaper && (
                      <Button
                        variant="outlined"
                        onClick={handleCancelEdit}
                        startIcon={<CancelIcon />}
                        sx={{
                          flex: 1,
                          py: 2,
                          borderRadius: 3,
                          borderWidth: 2,
                          borderColor: '#667eea',
                          color: '#667eea',
                          fontSize: '18px',
                          fontWeight: 700,
                          textTransform: 'none',
                          '&:hover': {
                            borderWidth: 2,
                            borderColor: '#764ba2',
                            background: alpha('#667eea', 0.05),
                            transform: 'translateY(-3px)'
                          }
                        }}
                      >
                        Cancel Edit
                      </Button>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </form>
          </MuiPaper>
          </Box>
        </Slide>
      )}

      {/* Fancy View Dialog */}
      <Dialog 
        open={viewDialog} 
        onClose={() => setViewDialog(false)} 
        maxWidth="md" 
        fullWidth
        TransitionComponent={Zoom}
        PaperProps={{
          sx: {
            borderRadius: 4,
            boxShadow: '0 24px 80px rgba(0,0,0,0.3)'
          }
        }}
      >
        <DialogTitle sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          py: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ 
              width: 50, 
              height: 50, 
              background: 'rgba(255,255,255,0.2)',
              backdropFilter: 'blur(10px)'
            }}>
              <ViewIcon sx={{ fontSize: 28 }} />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={700}>Paper Details</Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                Complete information about this paper
              </Typography>
            </Box>
          </Box>
          <IconButton 
            onClick={() => setViewDialog(false)}
            sx={{ 
              color: 'white',
              background: 'rgba(255,255,255,0.1)',
              '&:hover': {
                background: 'rgba(255,255,255,0.2)'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          {viewingPaper && (
            <Box>
              <Box sx={{ 
                mb: 3, 
                pb: 3, 
                borderBottom: '2px solid',
                borderColor: 'divider'
              }}>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, color: '#667eea' }}>
                  {viewingPaper.name}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                  {viewingPaper.description || 'No description provided'}
                </Typography>
              </Box>
              
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={4}>
                  <Box sx={{ 
                    p: 2, 
                    background: alpha('#667eea', 0.05),
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: alpha('#667eea', 0.2)
                  }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      Category
                    </Typography>
                    <Typography variant="body1" fontWeight={700} color="#667eea">
                      {viewingPaper.category_name}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box sx={{ 
                    p: 2, 
                    background: alpha('#764ba2', 0.05),
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: alpha('#764ba2', 0.2)
                  }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      Format
                    </Typography>
                    <Typography variant="body1" fontWeight={700} color="#764ba2">
                      {viewingPaper.format}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box sx={{ 
                    p: 2, 
                    background: alpha('#4caf50', 0.05),
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: alpha('#4caf50', 0.2)
                  }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      Price
                    </Typography>
                    <Typography variant="h6" fontWeight={700} color="#4caf50">
                      {viewingPaper.currency}{viewingPaper.price}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} md={4}>
                  <Box sx={{
                    p: 2,
                    background: alpha('#0288d1', 0.05),
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: alpha('#0288d1', 0.2)
                  }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      School year
                    </Typography>
                    <Typography variant="body1" fontWeight={700} color="#01579b">
                      {viewingPaper.school_year_name || '—'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={8}>
                  <Box sx={{
                    p: 2,
                    background: alpha('#7c3aed', 0.05),
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: alpha('#7c3aed', 0.2)
                  }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" sx={{ mb: 1 }}>
                      Subjects
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(viewingPaper.subjects || []).length > 0
                        ? viewingPaper.subjects.map((s) => (
                            <Chip
                              key={typeof s === 'object' ? s.id : s}
                              label={typeof s === 'object' ? s.name : s}
                              size="small"
                              sx={{ fontWeight: 600 }}
                            />
                          ))
                        : (
                          <Typography variant="body2" color="text.secondary">
                            {viewingPaper.subject_name || '—'}
                          </Typography>
                        )}
                    </Box>
                  </Box>
                </Grid>
              </Grid>

              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} md={4}>
                  <Box sx={{
                    p: 2,
                    background: alpha('#ff9800', 0.05),
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: alpha('#ff9800', 0.2)
                  }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      Total marks
                    </Typography>
                    {(() => {
                      const declared = viewingPaper.total_marks ?? 0;
                      const assigned = viewingPaper.extract_marks_total ?? 0;
                      const mismatch = assigned > 0 && declared > 0 && assigned !== declared;
                      return (
                        <>
                          <Typography variant="body1" fontWeight={700} color={mismatch ? '#c62828' : '#e65100'}>
                            {(!declared && !assigned) ? '—' : (mismatch ? `${assigned} / ${declared}` : (declared || assigned))}
                          </Typography>
                          {mismatch && (
                            <Typography variant="caption" color="text.secondary">
                              {assigned} assigned · {declared} declared
                            </Typography>
                          )}
                        </>
                      );
                    })()}
                  </Box>
                </Grid>
              </Grid>

              {(() => {
                const links = Array.isArray(viewingPaper.source_links) ? [...viewingPaper.source_links] : [];
                const primary = viewingPaper.original_paper_link;
                if (primary && !links.some((l) => (typeof l === 'string' ? l : l.url) === primary)) {
                  links.unshift({ url: primary, title: primary, is_official: false });
                }
                const isBrokenLink = (l) => typeof l !== 'string' && l?.status === 'broken';
                links.sort((a, b) =>
                  (isBrokenLink(a) ? 1 : 0) - (isBrokenLink(b) ? 1 : 0)
                  || (b?.is_official ? 1 : 0) - (a?.is_official ? 1 : 0)
                );

                const handleDeleteLink = (idx) => {
                  const url = typeof links[idx] === 'string' ? links[idx] : links[idx].url;
                  const filtered = links.filter((_, j) => j !== idx);
                  handleViewSourceLinkSave(filtered, primary === url ? null : primary);
                };

                const handleSetPrimary = (url) => {
                  handleViewSourceLinkSave(links, url);
                };

                const handleSaveEdit = () => {
                  if (!viewLinkEdit) return;
                  if (viewLinkEdit.isAdd) {
                    if (!viewLinkEdit.url.trim()) return;
                    const newLink = { url: viewLinkEdit.url.trim(), title: viewLinkEdit.title.trim() || viewLinkEdit.url.trim(), is_official: viewLinkEdit.is_official };
                    handleViewSourceLinkSave([...links, newLink], primary);
                  } else {
                    const updated = links.map((l, j) =>
                      j === viewLinkEdit.idx
                        ? { url: viewLinkEdit.url.trim(), title: viewLinkEdit.title.trim() || viewLinkEdit.url.trim(), is_official: viewLinkEdit.is_official }
                        : l
                    );
                    const oldUrl = typeof links[viewLinkEdit.idx] === 'string' ? links[viewLinkEdit.idx] : links[viewLinkEdit.idx].url;
                    handleViewSourceLinkSave(updated, primary === oldUrl ? viewLinkEdit.url.trim() : primary);
                  }
                };

                return (
                  <Box sx={{ mb: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: '#00838f', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LinkIcon sx={{ color: '#00bcd4' }} /> Source links {links.length > 0 ? `(${links.length})` : ''}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<AddIcon />}
                          onClick={() => setViewLinkEdit({ isAdd: true, url: '', title: '', is_official: false })}
                          sx={{ textTransform: 'none', borderRadius: 1.5, fontSize: '0.75rem' }}
                        >
                          Add link
                        </Button>
                        {links.length > 0 && (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={linkCheckRunning ? <CircularProgress size={14} /> : <HealthCheckIcon />}
                            disabled={linkCheckRunning}
                            onClick={handleCheckLinks}
                            sx={{ textTransform: 'none', borderRadius: 1.5, fontSize: '0.75rem' }}
                          >
                            Check links
                          </Button>
                        )}
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={reSearchRunning ? <CircularProgress size={14} /> : <TravelExploreIcon />}
                          disabled={reSearchRunning}
                          onClick={handleReSearchInDetails}
                          sx={{ textTransform: 'none', borderRadius: 1.5, fontSize: '0.75rem', borderColor: '#00bcd4', color: '#00838f' }}
                        >
                          Re-search AI
                        </Button>
                      </Box>
                    </Box>

                    {links.length === 0 && !viewLinkEdit?.isAdd ? (
                      <Typography variant="body2" color="text.secondary">No source links found yet.</Typography>
                    ) : (
                      <List disablePadding sx={{ background: alpha('#00bcd4', 0.05), borderRadius: 2, border: '1px solid', borderColor: alpha('#00bcd4', 0.2) }}>
                        {links.map((l, i) => {
                          const url = typeof l === 'string' ? l : l.url;
                          const title = typeof l === 'string' ? l : (l.title || l.url);
                          const isOfficial = typeof l !== 'string' && !!l.is_official;
                          const isPrimary = url === primary;
                          const isBroken = isBrokenLink(l);
                          const httpStatus = typeof l !== 'string' ? l.http_status : null;
                          const linkColor = isBroken ? '#d32f2f' : (isPrimary ? '#e65100' : '#1976d2');
                          const isEditing = viewLinkEdit && !viewLinkEdit.isAdd && viewLinkEdit.idx === i;

                          return (
                            <ListItem key={i} divider={i < links.length - 1 || !!viewLinkEdit?.isAdd} sx={{ flexDirection: 'column', alignItems: 'stretch', py: isEditing ? 1.5 : 1 }}>
                              {isEditing ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                  <TextField
                                    size="small"
                                    label="URL"
                                    fullWidth
                                    value={viewLinkEdit.url}
                                    onChange={(e) => setViewLinkEdit((prev) => ({ ...prev, url: e.target.value }))}
                                    autoFocus
                                  />
                                  <TextField
                                    size="small"
                                    label="Title"
                                    fullWidth
                                    value={viewLinkEdit.title}
                                    onChange={(e) => setViewLinkEdit((prev) => ({ ...prev, title: e.target.value }))}
                                  />
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        size="small"
                                        checked={viewLinkEdit.is_official}
                                        onChange={(e) => setViewLinkEdit((prev) => ({ ...prev, is_official: e.target.checked }))}
                                      />
                                    }
                                    label={<Typography variant="caption">Official source</Typography>}
                                  />
                                  <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button size="small" variant="contained" disabled={viewLinkSaving || !viewLinkEdit.url.trim()} onClick={handleSaveEdit} sx={{ textTransform: 'none', borderRadius: 1.5 }}>
                                      {viewLinkSaving ? <CircularProgress size={14} /> : 'Save'}
                                    </Button>
                                    <Button size="small" onClick={() => setViewLinkEdit(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
                                  </Box>
                                </Box>
                              ) : (
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, width: '100%' }}>
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                      {isBroken && (
                                        <Tooltip title={`Link broken (HTTP ${httpStatus || 'unreachable'})`}>
                                          <CancelIcon sx={{ fontSize: 16, color: '#d32f2f' }} />
                                        </Tooltip>
                                      )}
                                      <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: linkColor, wordBreak: 'break-all', fontWeight: isPrimary ? 700 : 400, textDecoration: isBroken ? 'line-through' : 'underline' }}>
                                        {title}
                                      </a>
                                      {isPrimary && (
                                        <Chip label="Primary" size="small" color="warning" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700 }} />
                                      )}
                                      <Chip label={isOfficial ? 'Original' : 'Other'} size="small" color={isOfficial ? 'success' : 'default'} sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700 }} />
                                      {isBroken && (
                                        <Chip label="Broken" size="small" color="error" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700 }} />
                                      )}
                                    </Box>
                                    {title !== url && (
                                      <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all', fontSize: '0.72rem', display: 'block', mt: 0.25 }}>
                                        {url}
                                      </Typography>
                                    )}
                                  </Box>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                                    {!isPrimary && (
                                      <Tooltip title="Set as primary link">
                                        <IconButton size="small" disabled={viewLinkSaving} onClick={() => handleSetPrimary(url)} sx={{ color: '#ff9800' }}>
                                          <StarBorderIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                    <Tooltip title="Edit">
                                      <IconButton size="small" onClick={() => setViewLinkEdit({ idx: i, url, title: title === url ? '' : title, is_official: isOfficial })} sx={{ color: '#1976d2' }}>
                                        <EditIcon sx={{ fontSize: 16 }} />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete">
                                      <IconButton size="small" disabled={viewLinkSaving} onClick={() => handleDeleteLink(i)} sx={{ color: '#d32f2f' }}>
                                        <DeleteIcon sx={{ fontSize: 16 }} />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                </Box>
                              )}
                            </ListItem>
                          );
                        })}

                        {viewLinkEdit?.isAdd && (
                          <ListItem divider={false} sx={{ flexDirection: 'column', alignItems: 'stretch', py: 1.5, borderTop: links.length > 0 ? '1px solid' : 'none', borderColor: 'divider' }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                              <TextField
                                size="small"
                                label="URL"
                                fullWidth
                                value={viewLinkEdit.url}
                                onChange={(e) => setViewLinkEdit((prev) => ({ ...prev, url: e.target.value }))}
                                autoFocus
                                placeholder="https://"
                              />
                              <TextField
                                size="small"
                                label="Title (optional)"
                                fullWidth
                                value={viewLinkEdit.title}
                                onChange={(e) => setViewLinkEdit((prev) => ({ ...prev, title: e.target.value }))}
                              />
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    size="small"
                                    checked={viewLinkEdit.is_official}
                                    onChange={(e) => setViewLinkEdit((prev) => ({ ...prev, is_official: e.target.checked }))}
                                  />
                                }
                                label={<Typography variant="caption">Official source</Typography>}
                              />
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button size="small" variant="contained" disabled={viewLinkSaving || !viewLinkEdit.url.trim()} onClick={handleSaveEdit} sx={{ textTransform: 'none', borderRadius: 1.5 }}>
                                  {viewLinkSaving ? <CircularProgress size={14} /> : 'Add'}
                                </Button>
                                <Button size="small" onClick={() => setViewLinkEdit(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
                              </Box>
                            </Box>
                          </ListItem>
                        )}
                      </List>
                    )}
                  </Box>
                );
              })()}

              {viewingPaper.questions && viewingPaper.questions.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: '#667eea', display: 'flex', alignItems: 'center', gap: 1 }}>
                    Questions ({viewingPaper.questions.length})
                  </Typography>
                  <List sx={{ background: alpha('#667eea', 0.05), borderRadius: 2, border: '1px solid', borderColor: alpha('#667eea', 0.2) }}>
                    {viewingPaper.questions.map((q, idx) => (
                      <ListItem key={q.id || idx} sx={{ flexDirection: 'column', alignItems: 'flex-start', borderBottom: idx < viewingPaper.questions.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}>
                        <Box
                          sx={{
                            width: '100%',
                            mb: 1,
                            '& p': { marginTop: 0, marginBottom: 0.5 },
                            '& img': { maxWidth: '100%', height: 'auto' },
                          }}
                          dangerouslySetInnerHTML={{ __html: q.question_text || '' }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          Marks: {q.marks} · Duration: {q.duration_in_sec}s · Correct: {q.correct_answer}
                          {q.is_free ? ' · Free sample' : ''}
                        </Typography>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, background: alpha('#667eea', 0.03) }}>
          <Button 
            onClick={() => setViewDialog(false)}
            variant="contained"
            sx={{
              borderRadius: 2,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              textTransform: 'none',
              fontWeight: 600,
              px: 4
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Fancy Delete Dialog */}
      <Dialog 
        open={deleteDialog.open} 
        onClose={() => setDeleteDialog({ open: false, paper: null, deleteQuestionBankToo: false, forceDelete: false })} 
        maxWidth="sm" 
        fullWidth
        TransitionComponent={Zoom}
        PaperProps={{
          sx: {
            borderRadius: 4,
            boxShadow: '0 24px 80px rgba(244, 67, 54, 0.3)'
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)', 
          color: 'white',
          py: 3
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ 
              width: 50, 
              height: 50, 
              background: 'rgba(255,255,255,0.2)',
              backdropFilter: 'blur(10px)'
            }}>
              <DeleteIcon sx={{ fontSize: 28 }} />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={700}>Delete Paper</Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                This action cannot be undone
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          <Typography variant="body1" sx={{ mb: 3, fontSize: '16px' }}>
            Are you sure you want to delete this paper?
          </Typography>
          {deleteDialog.paper && (
            <Box sx={{ 
              p: 3, 
              background: alpha('#f44336', 0.05),
              borderRadius: 2,
              border: '2px solid',
              borderColor: alpha('#f44336', 0.2)
            }}>
              <Typography variant="subtitle1" fontWeight={700} color="#f44336" sx={{ mb: 1 }}>
                {deleteDialog.paper.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {deleteDialog.paper.description?.substring(0, 100)}...
              </Typography>
            </Box>
          )}
          <Alert 
            severity="error" 
            icon={<DeleteIcon />}
            sx={{ 
              mt: 3,
              borderRadius: 2,
              border: '2px solid',
              borderColor: alpha('#f44336', 0.3)
            }}
          >
            <Typography variant="body2" fontWeight={700}>
              Warning: This action cannot be undone!
            </Typography>
          </Alert>
          <FormControlLabel
            sx={{ mt: 2 }}
            control={
              <Checkbox
                checked={deleteDialog.deleteQuestionBankToo}
                onChange={(e) => setDeleteDialog((prev) => ({
                  ...prev,
                  deleteQuestionBankToo: e.target.checked,
                }))}
                disabled={!canDeleteQuestionBank}
              />
            }
            label="Also delete related Question Bank questions"
          />
          {deleteDialog.deleteQuestionBankToo && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              Related Question Bank questions for this paper will be deleted only if they are not linked to mock exams.
            </Alert>
          )}
          {!canDeleteQuestionBank && (
            <Typography variant="caption" color="text.secondary">
              You do not have permission to delete Question Bank questions.
            </Typography>
          )}
          <FormControlLabel
            sx={{ mt: 2 }}
            control={
              <Checkbox
                checked={deleteDialog.forceDelete}
                onChange={(e) => setDeleteDialog((prev) => ({
                  ...prev,
                  forceDelete: e.target.checked,
                }))}
                color="error"
              />
            }
            label="Force delete (ignore purchases)"
          />
          {deleteDialog.forceDelete && (
            <Alert severity="error" sx={{ mt: 1 }}>
              This will permanently delete the paper even if it has been purchased by parents or students. Their access will be revoked.
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2, background: alpha('#f44336', 0.03) }}>
          <Button 
            onClick={() => setDeleteDialog({ open: false, paper: null, deleteQuestionBankToo: false, forceDelete: false })}
            variant="outlined"
            sx={{
              flex: 1,
              borderRadius: 2,
              borderWidth: 2,
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            disabled={deletingPaperId !== null}
            startIcon={deletingPaperId !== null ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
            sx={{
              flex: 1,
              borderRadius: 2,
              background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
              textTransform: 'none',
              fontWeight: 700,
              boxShadow: '0 8px 24px rgba(244, 67, 54, 0.4)',
              '&:hover': {
                boxShadow: '0 12px 32px rgba(244, 67, 54, 0.5)'
              }
            }}
          >
            {deletingPaperId !== null ? 'Deleting...' : 'Delete Paper'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove Question from Paper Dialog */}
      <Dialog
        open={removeQDialog.open}
        onClose={() => setRemoveQDialog({ open: false, question: null })}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Zoom}
        PaperProps={{
          sx: {
            borderRadius: 4,
            boxShadow: '0 24px 80px rgba(244, 67, 54, 0.3)',
          }
        }}
      >
        <DialogTitle sx={{
          background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
          color: 'white',
          py: 3,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{
              width: 50,
              height: 50,
              background: 'rgba(255,255,255,0.2)',
              backdropFilter: 'blur(10px)',
            }}>
              <DeleteIcon sx={{ fontSize: 28 }} />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={700}>Remove Question</Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                This action cannot be undone
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          <Typography variant="body1" sx={{ mb: 3, fontSize: '16px' }}>
            Remove this question from the paper?
          </Typography>
          <Alert
            severity="error"
            icon={<DeleteIcon />}
            sx={{
              borderRadius: 2,
              border: '2px solid',
              borderColor: alpha('#f44336', 0.3),
            }}
          >
            <Typography variant="body2" fontWeight={700}>
              Warning: This action cannot be undone!
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2, background: alpha('#f44336', 0.03) }}>
          <Button
            onClick={() => setRemoveQDialog({ open: false, question: null })}
            variant="outlined"
            sx={{
              flex: 1,
              borderRadius: 2,
              borderWidth: 2,
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              removePaperExtractQuestion(removeQDialog.question?.id);
              setRemoveQDialog({ open: false, question: null });
            }}
            variant="contained"
            disabled={paperExtractRemovingId !== null}
            startIcon={paperExtractRemovingId !== null ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
            sx={{
              flex: 1,
              borderRadius: 2,
              background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
              textTransform: 'none',
              fontWeight: 700,
              boxShadow: '0 8px 24px rgba(244, 67, 54, 0.4)',
              '&:hover': {
                boxShadow: '0 12px 32px rgba(244, 67, 54, 0.5)',
              },
            }}
          >
            {paperExtractRemovingId !== null ? 'Removing…' : 'Remove Question'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove Sub-Question Dialog */}
      <Dialog
        open={removeSubQDialog.open}
        onClose={() => setRemoveSubQDialog({ open: false, parentQ: null, subIdx: null, label: '' })}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Zoom}
        PaperProps={{
          sx: {
            borderRadius: 4,
            boxShadow: '0 24px 80px rgba(244, 67, 54, 0.3)',
          }
        }}
      >
        <DialogTitle sx={{
          background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
          color: 'white',
          py: 3,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{
              width: 50,
              height: 50,
              background: 'rgba(255,255,255,0.2)',
              backdropFilter: 'blur(10px)',
            }}>
              <DeleteIcon sx={{ fontSize: 28 }} />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={700}>Remove Sub-question</Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                This action cannot be undone
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          <Typography variant="body1" sx={{ mb: 3, fontSize: '16px' }}>
            Remove sub-question <strong>"{removeSubQDialog.label}"</strong> from the paper?
          </Typography>
          <Alert
            severity="error"
            icon={<DeleteIcon />}
            sx={{
              borderRadius: 2,
              border: '2px solid',
              borderColor: alpha('#f44336', 0.3),
            }}
          >
            <Typography variant="body2" fontWeight={700}>
              Warning: This action cannot be undone!
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2, background: alpha('#f44336', 0.03) }}>
          <Button
            onClick={() => setRemoveSubQDialog({ open: false, parentQ: null, subIdx: null, label: '' })}
            variant="outlined"
            sx={{
              flex: 1,
              borderRadius: 2,
              borderWidth: 2,
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmRemoveSubQuestion}
            variant="contained"
            startIcon={<DeleteIcon />}
            sx={{
              flex: 1,
              borderRadius: 2,
              background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
              textTransform: 'none',
              fontWeight: 700,
              boxShadow: '0 8px 24px rgba(244, 67, 54, 0.4)',
              '&:hover': {
                boxShadow: '0 12px 32px rgba(244, 67, 54, 0.5)',
              },
            }}
          >
            Remove Sub-question
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Preview Dialog */}
      <Dialog
        open={imagePreview.open}
        onClose={handleCloseImagePreview}
        maxWidth="md"
        fullWidth
        TransitionComponent={Zoom}
        PaperProps={{
          sx: {
            borderRadius: 4,
            boxShadow: '0 24px 80px rgba(0,0,0,0.3)',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ 
              width: 40, 
              height: 40, 
              background: 'rgba(255,255,255,0.2)'
            }}>
              <ImageIcon />
            </Avatar>
            <Typography variant="h6" fontWeight={600}>Image Preview</Typography>
          </Box>
          <IconButton 
            onClick={handleCloseImagePreview}
            sx={{ 
              color: 'white',
              background: 'rgba(255,255,255,0.1)',
              '&:hover': {
                background: 'rgba(255,255,255,0.2)'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400, background: '#f5f5f5' }}>
          {imagePreview.url && (
            <img 
              src={imagePreview.url} 
              alt="Preview" 
              style={{ 
                maxWidth: '100%', 
                maxHeight: '70vh',
                objectFit: 'contain'
              }} 
            />
          )}
        </DialogContent>
      </Dialog>

      {/* ── Source Link Picker Dialog ─────────────────────────────────────── */}
      <Dialog
        open={findLinkDialogOpen}
        onClose={() => setFindLinkDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TravelExploreIcon sx={{ color: '#00bcd4' }} />
            Find Source Link
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontWeight: 400 }}>
            {findLinkPaper?.name}
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {findLinkLoading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 5, gap: 2 }}>
              <CircularProgress sx={{ color: '#00bcd4' }} />
              <Typography variant="body2" color="text.secondary">Searching with Gemini…</Typography>
            </Box>
          ) : findLinkError ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography color="error">Search failed.</Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                The AI search could not be completed. Click Refresh to try again.
              </Typography>
            </Box>
          ) : findLinkCandidates.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">No source links found for this paper.</Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                Try editing the paper name to be more specific, then click Refresh.
              </Typography>
            </Box>
          ) : (
            <List disablePadding>
              {[...findLinkCandidates]
                .sort((a, b) =>
                  ((a?.status === 'broken') ? 1 : 0) - ((b?.status === 'broken') ? 1 : 0)
                  || (b?.is_official ? 1 : 0) - (a?.is_official ? 1 : 0)
                )
                .map((c, i, arr) => {
                const url   = typeof c === 'string' ? c : c.url;
                const title = typeof c === 'string' ? c : (c.title || c.url);
                const isOfficial = typeof c !== 'string' && !!c.is_official;
                const isBroken = typeof c !== 'string' && c?.status === 'broken';
                return (
                  <ListItem
                    key={i}
                    divider={i < arr.length - 1}
                    sx={{ pr: 13 }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          {isBroken && (
                            <Tooltip title={`Link broken (HTTP ${(typeof c !== 'string' && c.http_status) || 'unreachable'})`}>
                              <CancelIcon sx={{ fontSize: 16, color: '#d32f2f' }} />
                            </Tooltip>
                          )}
                          <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: isBroken ? '#d32f2f' : '#1976d2', wordBreak: 'break-all', textDecoration: isBroken ? 'line-through' : 'underline' }}>
                            {title}
                          </a>
                          <Chip label={isOfficial ? 'Original' : 'Other'} size="small" color={isOfficial ? 'success' : 'default'} sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700 }} />
                          {isBroken && (
                            <Chip label="Broken" size="small" color="error" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700 }} />
                          )}
                        </Box>
                      }
                      secondary={title !== url ? url : null}
                      secondaryTypographyProps={{ sx: { wordBreak: 'break-all', fontSize: '0.72rem' } }}
                    />
                    <ListItemSecondaryAction>
                      <Button
                        size="small"
                        variant="contained"
                        disabled={findLinkSaving}
                        onClick={() => handleSelectSourceLink(url)}
                        sx={{ textTransform: 'none', borderRadius: 1.5, minWidth: 60 }}
                      >
                        Use
                      </Button>
                    </ListItemSecondaryAction>
                  </ListItem>
                );
              })}
            </List>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5 }}>
          <Button
            size="small"
            startIcon={findLinkLoading ? <CircularProgress size={14} /> : <SearchIcon />}
            disabled={findLinkLoading}
            onClick={() => findLinkPaper && handleFindSourceLink(findLinkPaper)}
            sx={{ textTransform: 'none' }}
          >
            Refresh
          </Button>
          <Button onClick={() => setFindLinkDialogOpen(false)} sx={{ textTransform: 'none' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign question bank → paper extract (same pattern as Mock Exam) */}
      <Dialog
        open={assignQbOpen}
        onClose={closeAssignQuestionBank}
        maxWidth="md"
        fullWidth
        keepMounted={false}
        aria-modal="true"
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          Assign question bank
          <Typography variant="body2" color="text.secondary" component="span" display="block" sx={{ mt: 0.5 }}>
            {assignQbPaper?.name || ''}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {assignQbLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                <Button
                  variant="outlined"
                  startIcon={<QuizIcon />}
                  onClick={() => {
                    setBankPickerOpen(true);
                    setBankPickerSelected([]);
                  }}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  Add from question bank
                </Button>
                {assignQbSelected.length > 0 && (
                  <Chip label={`${assignQbSelected.length} selected`} size="small" color="primary" />
                )}
              </Box>
              {assignQbSelected.length === 0 ? (
                <Typography color="text.secondary" sx={{ py: 2 }}>
                  No questions yet. Use &quot;Add from question bank&quot; to pick questions (same flow as Mock Exams).
                </Typography>
              ) : (
                <List dense sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1 }}>
                  {assignQbSelected.map((q) => (
                    <ListItem
                      key={q.id}
                      secondaryAction={
                        <IconButton edge="end" size="small" onClick={() => removeAssignQbQuestion(q.id)} aria-label="Remove">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      }
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 0.5 }}>
                            <Chip label={`ID: ${q.id}`} size="small" variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />
                            <Chip
                              label={(q.type || 'question').replace(/-/g, ' ')}
                              size="small"
                              variant="outlined"
                              sx={{ height: 22, fontSize: '0.7rem' }}
                            />
                          </Box>
                        }
                        secondary={
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mt: 0.5 }}>
                            {q.image_url && (
                              <Box sx={{ flexShrink: 0 }}>
                                <img
                                  src={q.image_url}
                                  alt=""
                                  style={{ width: 72, height: 54, objectFit: 'cover', borderRadius: 6, border: '1px solid #e0e0e0' }}
                                />
                              </Box>
                            )}
                            <Box sx={{ minWidth: 0, maxHeight: 240, overflow: 'auto' }}>
                              <HtmlWithLatex html={q.question_text || ''} />
                            </Box>
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button onClick={closeAssignQuestionBank} disabled={assignQbSaving} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={saveAssignQuestionBank}
            disabled={assignQbSaving || assignQbLoading || assignQbSelected.length === 0}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              background: 'linear-gradient(to right, #3f2b96, #667eea)',
            }}
          >
            {assignQbSaving ? 'Saving…' : 'Save extract'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={bankPickerOpen}
        onClose={() => setBankPickerOpen(false)}
        maxWidth="md"
        fullWidth
        keepMounted={false}
        aria-modal="true"
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          Select questions from bank
          <Typography variant="body2" color="text.secondary">
            Choose questions to attach to this paper&apos;s extract.
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              size="small"
              placeholder="Search questions…"
              value={bankPickerSearch}
              onChange={(e) => {
                setBankPickerSearch(e.target.value);
                setBankPickerPage(0);
              }}
              sx={{ flex: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <Autocomplete
              multiple
              size="small"
              sx={{ minWidth: 240 }}
              options={topicOptions || []}
              getOptionLabel={(o) => o?.label || o?.name || ""}
              isOptionEqualToValue={(a, b) => Number(a?.id) === Number(b?.id)}
              value={(topicOptions || []).filter((o) => bankPickerTopicIds.includes(Number(o.id)))}
              onChange={(_, vals) => {
                setBankPickerTopicIds(vals.map((v) => Number(v.id)));
                setBankPickerPage(0);
              }}
              renderInput={(params) => <TextField {...params} label="Topics" />}
            />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>All Types</InputLabel>
              <Select
                value={bankPickerType}
                label="All Types"
                onChange={(e) => {
                  setBankPickerType(e.target.value);
                  setBankPickerPage(0);
                }}
              >
                <MenuItem value="">All Types</MenuItem>
                <MenuItem value="multiple-choice">Multiple Choice</MenuItem>
                <MenuItem value="multiple-select">Multiple Select</MenuItem>
                <MenuItem value="true-false">True/False</MenuItem>
                <MenuItem value="fill-in-the-blank">Fill in the Blank</MenuItem>
                <MenuItem value="short-answer">Short Answer</MenuItem>
                <MenuItem value="essay">Essay</MenuItem>
                <MenuItem value="matching">Matching</MenuItem>
                <MenuItem value="ordering">Ordering</MenuItem>
              </Select>
            </FormControl>
          </Box>
          {bankPickerLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : bankPickerQuestions.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
              No questions found.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {bankPickerQuestions.map((q) => {
                const alreadyAdded = assignQbSelected.some((sq) => sq.id === q.id);
                const inCurrentSelection = bankPickerSelected.some((sq) => sq.id === q.id);
                return (
                  <MuiPaper
                    key={q.id}
                    variant="outlined"
                    onClick={() => toggleBankPickerSelect(q)}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 2,
                      cursor: 'pointer',
                      borderColor: inCurrentSelection ? 'primary.main' : '#e0e0e0',
                      bgcolor: inCurrentSelection ? 'primary.50' : 'transparent',
                      transition: 'all 0.15s',
                      '&:hover': { bgcolor: inCurrentSelection ? 'primary.50' : '#f5f5f5' },
                    }}
                  >
                    <Checkbox
                      checked={inCurrentSelection || alreadyAdded}
                      size="small"
                      sx={{ mt: -0.5 }}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => toggleBankPickerSelect(q)}
                    />
                    {q.image_url && (
                      <Box sx={{ flexShrink: 0 }}>
                        <img
                          src={q.image_url}
                          alt=""
                          style={{ width: 80, height: 60, objectFit: 'cover', borderRadius: 6, border: '1px solid #e0e0e0' }}
                        />
                      </Box>
                    )}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                        <Chip label={`ID: ${q.id}`} size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: 20 }} />
                        <Chip
                          label={(q.type || '').replace(/-/g, ' ') || 'MCQ'}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.7rem', height: 20 }}
                        />
                        {alreadyAdded && <Chip label="Already added" size="small" color="success" sx={{ fontSize: '0.7rem', height: 20 }} />}
                      </Box>
                      <Box sx={{ maxHeight: 220, overflow: 'auto' }}>
                        <HtmlWithLatex html={q.question_text || ''} />
                      </Box>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                      {q.default_marks || 1}m
                    </Typography>
                  </MuiPaper>
                );
              })}
            </Box>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Page {bankPickerPage + 1} of {Math.max(1, Math.ceil(bankPickerTotal / 5))}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                disabled={bankPickerPage === 0}
                onClick={() => setBankPickerPage((p) => p - 1)}
                sx={{ textTransform: 'none' }}
              >
                Previous
              </Button>
              <Button
                size="small"
                disabled={(bankPickerPage + 1) * 5 >= bankPickerTotal}
                onClick={() => setBankPickerPage((p) => p + 1)}
                sx={{ textTransform: 'none' }}
              >
                Next
              </Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Typography variant="body2" sx={{ flex: 1, pl: 2, color: 'text.secondary' }}>
            {bankPickerSelected.length} question(s) in this page selection
          </Typography>
          <Button onClick={() => setBankPickerOpen(false)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={confirmBankPickerSelection}
            disabled={bankPickerSelected.length === 0}
            sx={{ background: 'linear-gradient(to right, #3f2b96, #667eea)', textTransform: 'none', fontWeight: 600 }}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>

      {editingBankQuestion && (
        <QuestionBankQuestionEditDialog
          question={editingBankQuestion}
          defaultSchoolYearId={editingPaper?.school_year_id ?? undefined}
          onClose={() => setEditingBankQuestion(null)}
          onSave={savePaperBankQuestionEdit}
          saving={bankQuestionSaving}
        />
      )}

      <CreateQuestionDialog
        open={addQDialog.open}
        onClose={() => !addQSaving && setAddQDialog({ open: false, mode: null, targetIdx: null, parentQ: null })}
        mode={addQDialog.mode}
        targetQ={addQDialog.parentQ ?? questionBankExtractPreview[addQDialog.targetIdx]}
        onSave={handleAddQuestionSave}
        saving={addQSaving}
      />

      <style>
        {`
          @keyframes slideInDown {
            from {
              opacity: 0;
              transform: translateY(-30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>

      <ConvertToMcqDialog
        open={Boolean(mcqConvertPaper)}
        onClose={() => setMcqConvertPaper(null)}
        paperId={mcqConvertPaper?.id}
        title={mcqConvertPaper?.name}
        defaultCategoryId={mcqConvertPaper?.category_id || ''}
      />
    </Box>
  );
};

export default Paper;
